import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  fetchDailySummary, fetchDotdigitalSignup, todayET,
  fetchVipOutreachRep, fetchVipOutreachStatus, logVipOutreach, undoVipOutreach,
  fetchShipInsertStatus, markShipInsert, undoShipInsert,
  fetchOutreachAssignees, fetchOutreachAssignments, assignOutreach,
  clearOutreachAssignments, pushOutreachAssignments, fetchOutreachScoreboard,
  fetchMyOpenOutreach, addOrderNote,
} from '../services/api';
import { getCurrentUser } from '../services/auth';
import { ChevronDown, ChevronRight, Crown, Sparkles, ArrowUp, ArrowDown, Download, Mail, Check, X as XIcon, Package, Users, Clock, AlertTriangle, Reply } from 'lucide-react';
import { buildEml, downloadEml, slugName, newSendToken, trackingReady, pixelTag } from '../lib/outreachEmail';
import { draftIntoOutlook } from '../lib/outreachDraft';
import VipWinbackPanel from '../components/VipWinbackPanel';
import OutreachRepliesPanel from '../components/OutreachRepliesPanel';
import NoteFollowupPanel from '../components/NoteFollowupPanel';
import VipSignupsPanel from '../components/VipSignupsPanel';
import VipGoalBoard from '../components/VipGoalBoard';
import OutreachDeliverability from '../components/OutreachDeliverability';
import DeliveredFollowup from '../components/DeliveredFollowup';

const money = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Time-of-day for a deadline chip, in the browser's clock (everyone using this
// screen is in Crisfield, and the reminder cron renders back in ET to match).
const fmtDue = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`;
};

// A local Date at h:mm on `dayOffset` days from today, as an ISO string.
const dueAtIso = (hour, dayOffset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
};

// Delegation is a manager's job — Rachael runs it. The server checks this too
// (outreach_assign_set reads roles off adm_users); this only decides what to
// draw, so a stale session can never quietly grant the power.
const canDelegate = (user) => {
  const roles = Array.isArray(user?.roles) ? user.roles : String(user?.roles || '').split(',');
  return roles.map((r) => String(r).trim().toLowerCase()).some((r) => r === 'manager' || r === 'admin');
};

// Order the cards render in, plus which detail layout each uses.
const CATEGORY_ORDER = [
  { key: 'existing_vip', showSignups: false, historyDetail: true },
  { key: 'ex_vip', showSignups: true, historyDetail: true },
  { key: 'repeat_never_vip', showSignups: true, historyDetail: true },
  { key: 'new', showSignups: true, historyDetail: false },
];

// ET-anchored date math off a YYYY-MM-DD string (noon avoids DST edges).
const shiftDays = (iso, n) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString('en-CA');
};
const firstOfMonth = (iso) => iso.slice(0, 8) + '01';
// [start, end] of the calendar month before the one `iso` falls in.
const lastMonthRange = (iso) => {
  const lastDayPrev = shiftDays(firstOfMonth(iso), -1); // day before the 1st = last day of prior month
  return [firstOfMonth(lastDayPrev), lastDayPrev];
};

// Last week's SHIPPING week, Tue through Sat — the days the call center works
// when they follow up on what delivered. Anchored on the Monday of the week
// `iso` falls in (Sunday counts as the tail of the week before it, the way the
// floor talks about it), so last Tuesday = that Monday - 6 and Saturday = -2.
const lastWeekTueSat = (iso) => {
  const dow = new Date(iso + 'T12:00:00').getDay(); // 0 Sun … 6 Sat
  const mondayOffset = dow === 0 ? -6 : 1 - dow;     // Sunday belongs to the week just ended
  const monday = shiftDays(iso, mondayOffset);
  return [shiftDays(monday, -6), shiftDays(monday, -2)];
};

// ⚠️ `?tab=` IS READ SO THE MORNING EMAIL'S BUTTON LANDS ON THE BOARD. Greg, 2026-09-19: "when I
// click the link it didn't default on that pill." A deep link that drops somebody one click short
// of the thing it promised is a link most people stop following.
// Only known ids are honoured; anything else falls through to the normal default rather than
// rendering an empty screen.
const TABS = ['goal', 'placed', 'delivered', 'shipping', 'notes', 'winback', 'signups', 'replies'];
// ⚠️ WHICH PILL THE PAGE OPENS ON, AND THE TWO ASKS BEHIND IT. Greg, 2026-09-25: "whatever pill I'm
// on, if I hard refresh, can you keep me on that pill", and the same afternoon: "when we land on
// the order summary page, can we land it on today's goal". Both hold because they are different
// moments:
//   * ARRIVING (sidebar, a link, a fresh login) opens on TODAY'S GOAL.
//   * A REFRESH while on this page reopens the pill you were on.
// Told apart by what App.js had in sessionStorage `cpx_page` when the document loaded - read ONCE
// at module load, which is app start, before any navigation - and by only honouring it on the
// FIRST mount of this page in that document. Later mounts are arrivals.
// A deep link's ?tab= also counts on the first mount only, so the morning email still lands where
// it promises but its ?tab=, which lingers in the address bar, cannot hijack a later arrival.
// This replaced Delivered by day as the default (Rachael's pick, 2026-08-31) at Greg's request.
const DEFAULT_TAB = 'goal';
const TAB_KEY = 'cpx_summary_tab';
const LOADED_ON_THIS_PAGE = (() => {
  try { return window.sessionStorage.getItem('cpx_page') === 'daily-summary'; } catch (_) { return false; }
})();
let firstMount = true;
const urlTab = () => {
  if (!firstMount) return DEFAULT_TAB;
  try {
    const t = new URLSearchParams(window.location.search).get('tab');
    if (TABS.includes(t)) return t;
    if (LOADED_ON_THIS_PAGE) {
      const saved = window.sessionStorage.getItem(TAB_KEY);
      if (TABS.includes(saved)) return saved;
    }
  } catch (_) { /* blocked storage: fall through to the default */ }
  return DEFAULT_TAB;
};
const rememberTab = (t) => {
  try {
    window.sessionStorage.setItem(TAB_KEY, t);
    const qs = new URLSearchParams(window.location.search);
    if (qs.has('tab')) {
      qs.set('tab', t);
      window.history.replaceState(window.history.state, '', window.location.pathname + '?' + qs.toString() + window.location.hash);
    }
  } catch (_) { /* private mode or blocked storage: a refresh just lands on the default */ }
};
// The date range each tab opens on. Shared by the deep link and by changeMode, so a tab reached
// by URL and the same tab reached by a click can never show different days.
const rangeForTab = (tab, todayIso) => {
  if (tab === 'shipping') { const y = shiftDays(todayIso, -1); return [y, y]; }
  if (tab === 'delivered') return lastWeekTueSat(todayIso);
  return [todayIso, todayIso];
};

const rangeLabel = (start, end) => {
  const f = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return start === end ? f(start) : `${f(start)} – ${f(end)}`;
};

// ── VIP conversion outreach email ─────────────────────────────────────────────
// The CS team (Rachael/Linda/Ashley) emails non-VIP customers a VIP Club invite
// at a discount = $79.99 − what they would've saved on their recent order. If
// they'd have saved MORE than the membership costs, we hand it to them free.
// (Club price dropped $99.99 -> $79.99 per Greg, 2026-08-18.)

const MEMBERSHIP_PRICE = 79.99;
const OUTREACH_PHONE = '410-968-3167';
const OUTREACH_WEBSITE = 'www.crabplace.com';
// Per-rep signature titles, keyed by first name (lowercase). Anyone not listed
// falls back to a neutral "Customer Relations" title.
const REP_TITLES = {
  // A comma, not a dash: this prints in a customer email and dashes are off-brand there.
  rachael: 'Assistant Director, Customer Relations',
  linda: 'Customer Relations Specialist',
  ashley: 'Customer Relations Specialist',
};
const titleForName = (name) => REP_TITLES[firstNameOf(name).toLowerCase()] || 'Customer Relations';

function firstNameOf(name) {
  const n = String(name || '').trim();
  if (!n || n.toLowerCase().startsWith('(no name')) return 'there';
  return n.split(/\s+/)[0];
}

// Renewal = first of NEXT month, one year out (matches "sent today Jul 31 -> Aug
// 1, 2027"; sent Aug 1 -> Sept 1, 2027).
function renewalDateLabel(todayIso) {
  const [y, m] = todayIso.split('-').map(Number);
  let nm = m + 1, ny = y;
  if (nm > 12) { nm = 1; ny += 1; }
  ny += 1;
  return new Date(ny, nm - 1, 1, 12).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// $79.99 − would've-saved, floored at free. free=true when they'd have saved
// more than the membership costs.
function computeOffer(whs) {
  const saved = Math.max(0, Number(whs) || 0);
  const price = Math.round((MEMBERSHIP_PRICE - saved) * 100) / 100;
  return { saved, price: price <= 0 ? 0 : price, free: price <= 0 };
}

// Build the personalized VIP-invite email. Returns { to, subject, html, offer }.
function buildOutreachEmail(customer, rep, todayIso) {
  const first = firstNameOf(customer.name);
  const orders = Array.isArray(customer.orders) ? customer.orders : [];
  const latestOrder = orders[0]?.number || '';
  const orderWord = orders.length > 1 ? 'orders' : 'order';
  const offer = computeOffer(customer.whsPeriod);
  const renewal = renewalDateLabel(todayIso);
  const subject = latestOrder ? `CrabPlace.com Order #${latestOrder}` : 'The Crab Place VIP Club';
  // ⚠️ THE ORDER GOES IN THE BODY, NOT ONLY THE SUBJECT LINE. Rachael, 2026-09-01:
  // "when we email the customer, it's not noting the order." Every letter names it in the
  // opening line (orderNo below); only one of her 2026-09-14 drafts did.

  const repName = rep?.name || 'The Crab Place Team';
  const repEmail = rep?.email || 'customerservice@crabplace.com';
  const repTitle = rep?.title || titleForName(repName);

  // ⚠️ THREE LETTERS, ONE PER GROUP, IN RACHAEL'S WORDS (2026-09-14, adopted by Greg 2026-09-16).
  // She rewrote them after the old single letter read as pushy and repeated the offer several
  // times. Ex-VIPs are welcomed back, repeat customers are "let in on a little secret", brand-new
  // customers are thanked for choosing us. Anything else (an unknown category) gets the repeat
  // letter, which assumes the least.
  //
  // ⚠️ THE COMPARISON LINES ARE CONDITIONAL, BECAUSE THEY ARE ONLY SOMETIMES TRUE. "For less than
  // half of that amount" needs a saving above ~$53; "for less than what you would have saved" needs
  // one above ~$40. A customer who would have saved $30 is offered $49.99, and telling them that is
  // less than they would have saved is simply false. So each claim is printed only when the numbers
  // make it true.
  //
  // ⚠️ HER DRAFTS HAD NO FREE VERSION. When the saving beats the $79.99 membership the offer is a
  // free year, so each letter carries a free wording written in the same voice.
  const cat = customer.category === 'ex_vip' || customer.category === 'new' ? customer.category : 'repeat_never_vip';
  const saved = money(offer.saved);
  const price = money(offer.price);
  const orderNo = latestOrder ? ` #${escapeHtml(String(latestOrder))}` : '';
  const lessThanHalf = !offer.free && offer.price < offer.saved / 2;
  const lessThanSaved = !offer.free && offer.price < offer.saved;
  const b = (t) => `<strong>${t}</strong>`;

  const perks =
`<p style="margin:0 0 6px;">As a VIP Club member, you'll enjoy:</p>
<ul style="margin:0 0 14px;padding-left:22px;">
<li style="margin:0 0 4px;">${b('8% off seafood')}</li>
<li style="margin:0 0 4px;">${b('Discounted shipping')}</li>
<li style="margin:0 0 4px;">${b('3% cash back')} that never expires while your membership is active</li>
<li style="margin:0 0 4px;">${b('Free UPS insurance')} on orders over $100</li>
<li style="margin:0 0 4px;">${b('A $10 VIP code every month via SMS')} with no minimum purchase</li>
</ul>`;
  // "Over $72" matches the "average VIP saves $72.90 per order" the storefront publishes. Rachael's
  // drafts said $77, which no page on the site supports, and the win-back letter already says $72.
  const average = (tail) => `<p style="${'margin:0 0 14px;'}">Our VIP members save an average of over $72 per order${tail}</p>`;
  const bonus = `<p style="margin:0 0 14px;">And there's an added bonus: if you join with this offer, your membership won't renew until ${b(renewal)}, giving you extra time to enjoy your VIP benefits.</p>`;
  const P = (t) => `<p style="margin:0 0 14px;">${t}</p>`;
  const offerCta = offer.free
    ? `Interested in your free year of VIP? Simply reply to this email, and I'll take care of the rest!`
    : `Interested in taking advantage of your exclusive ${b(price)} offer? Simply reply to this email, and I'll take care of the rest!`;

  let bodyHtml;
  if (cat === 'ex_vip') {
    bodyHtml = [
      P(`Thank you for choosing The Crab Place! We truly appreciate your continued support and hope you enjoyed your recent order${orderNo}.`),
      P(`We noticed something we thought you'd want to know: you could have saved ${b(saved)} on your recent ${orderWord} as a VIP Club member.`),
      offer.free
        ? P(`Since you were previously a VIP member, we'd like to welcome you back with a ${b('full year of VIP membership at no charge')}. Our VIP membership is normally $79.99 per year, and what you would have saved is more than the membership itself, so it's yours free.`)
        : P(`Since you were previously a VIP member, we'd like to give you the opportunity to come back at a special price. Our VIP membership is normally ${b('$79.99 per year')}, but based on the savings you would have received on your recent order, we're offering you an exclusive sign-up price of just ${b(price)}!`),
      lessThanSaved ? P(`That's right, you'll receive all of the benefits of VIP for less than what you would have saved on your recent order!`) : '',
      perks,
      bonus.replace('if you join with this offer', 'if you take advantage of this offer'),
      P(`We know there are a lot of places you could choose to buy your seafood, and we're grateful you continue to choose The Crab Place. We'd love to have you back in the VIP Club!`),
      P(offerCta),
      P(`Thank you again for being a valued customer. We look forward to serving you again soon!`),
    ].join('\n');
  } else if (cat === 'new') {
    bodyHtml = [
      P(`Thank you for choosing The Crab Place! We hope you enjoyed everything on your recent order${orderNo}.`),
      P(`We know there are a lot of places you could choose to buy seafood, and we're grateful you chose us for your order. As a thank-you, we wanted to introduce you to our VIP Club and give you a special opportunity to join.`),
      offer.free
        ? P(`Our VIP membership is normally ${b('$79.99 per year')}, but because of your recent purchase, we're offering you a ${b('full year free')}. Here's why: if you had been a VIP member when you placed your recent ${orderWord}, you would have saved ${b(saved)}, which is more than the membership itself.`)
        : P(`Our VIP membership is normally ${b('$79.99 per year')}, but because of your recent purchase, we're offering you an exclusive limited-time price of just ${b(price)}.`),
      offer.free ? '' : P(`Here's why: if you had been a VIP member when you placed your recent ${orderWord}, you would have saved ${b(saved)}. Rather than letting you miss out on those savings, we're offering you a membership for just ${b(price)}!`),
      perks,
      average(', so the savings can add up quickly.'),
      bonus,
      P(`We'd love to have you as part of the VIP Club! ${offerCta}`),
      P(`Thank you again for choosing The Crab Place. We hope to serve you again soon!`),
    ].join('\n');
  } else {
    bodyHtml = [
      P(`Thank you for continuing to choose The Crab Place! We truly appreciate your continued support and hope you enjoyed everything on your recent order${orderNo}. Since you've ordered from us before, we wanted to let you in on a little secret: ${b(`you could have saved ${saved} on your recent ${orderWord} simply by being a VIP Club member.`)}`),
      offer.free
        ? P(`Our VIP membership is normally ${b('$79.99 per year')}, but because of your recent purchase and your history with The Crab Place, we'd like to give you a ${b('full year of VIP membership free')}. What you would have saved is more than the membership costs, so we'd rather hand it to you than let you keep missing out.`)
        : P(`Our VIP membership is normally ${b('$79.99 per year')}, but because of your recent purchase and your history with The Crab Place, we'd like to offer you an exclusive limited-time membership price of just ${b(price)}.`),
      offer.free ? '' : P(
        (lessThanHalf
          ? `In other words, ${b(`you would have saved ${saved} on your recent ${orderWord}, and we're offering you VIP membership for less than half of that amount.`)} `
          : lessThanSaved
            ? `In other words, ${b(`you would have saved ${saved} on your recent ${orderWord}, and we're offering you VIP membership for less than that.`)} `
            : '')
        + `We wanted to pass some of those potential savings back to you and give you the opportunity to start enjoying VIP benefits on your future orders.`),
      perks,
      average(`, so if you're planning to order from us again, the savings can add up quickly.`),
      bonus,
      P(`You've already made The Crab Place part of your seafood shopping, so why not make your next order even more rewarding?`),
      P(offerCta),
      P(`Thank you again for choosing The Crab Place. We look forward to serving you again soon!`),
    ].join('\n');
  }

  const html =
`<div style="font-family:Calibri,Arial,sans-serif;font-size:15px;line-height:1.5;color:#1a1a1a;">
<p style="margin:0 0 14px;">Hi ${escapeHtml(first)},</p>
${bodyHtml.split('\n').filter(Boolean).join('\n')}
<p style="margin:22px 0 0;font-weight:700;color:#00395c;">${escapeHtml(repName)}</p>
<p style="margin:0;font-size:12px;letter-spacing:0.3px;text-transform:uppercase;color:#475467;">${escapeHtml(repTitle)}</p>
<p style="margin:2px 0 0;font-size:13px;"><a href="mailto:${escapeHtml(repEmail)}" style="color:#00395c;">${escapeHtml(repEmail)}</a></p>
<p style="margin:0;font-size:13px;color:#475467;">${OUTREACH_PHONE}</p>
<p style="margin:0;font-size:13px;"><a href="https://${OUTREACH_WEBSITE}" style="color:#dc582a;">${OUTREACH_WEBSITE}</a></p>
</div>`;

  return { to: customer.email || '', subject, html, offer, latestOrder, latestOrderInternalId: orders[0]?.internalId || null };
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}


// The done_at of a customer's insert (first marked in-range order), for display.
const insertDoneDate = (c, insertMap) => {
  const o = (c.orders || []).find((x) => insertMap[String(x.number)]);
  return o ? insertMap[String(o.number)].doneAt : null;
};

export default function DailySummary({ navigateTo }) {
  const today = todayET();
  // ⚠️ THE PAGE OPENS ON LAST WEEK (Tue–Sat), not today, and that pairs with the
  // Delivered default below (Greg, 2026-09-09). A delivery follow-up queue keyed
  // on today is nearly empty by construction — the box has to land before anyone
  // can be called about it — so the range that actually has work in it is the
  // shipping week just finished. Today is one click away and unchanged.
  // ⚠️ The range has to agree with the tab the URL asked for. `changeMode` sets it on a click, but
  // a deep link never clicks - landing on Shipping carrying last week's dates would quietly show
  // the wrong day's work. Same table as changeMode, read once here.
  const [start, setStart] = useState(() => rangeForTab(urlTab(), today)[0]);
  const [end, setEnd] = useState(() => rangeForTab(urlTab(), today)[1]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({ existing_vip: false, ex_vip: false, repeat_never_vip: false, new: false, won_back: false });
  // 'placed' = the date range filters by ORDER date. 'delivered' = it filters by
  // ACTUAL DELIVERY date, so you can see what arrived in a window and follow up with
  // those customers post-delivery.
  // TODAY'S GOAL IS THE DEFAULT since 2026-09-25 (Greg). It was Delivered by day, at Rachael's
  // request - see urlTab for the history and for how a refresh keeps the current pill.
  // ⚠️ `?tab=` IS READ HERE, so the morning email's button lands on the board rather than on
  // whatever tab this page happens to open on. Greg, 2026-09-19: "when I click the link it didn't
  // default on that pill." A deep link that drops somebody one click short of the thing it
  // promised is a link most people stop following.
  // Only known tab ids are honoured - an unknown value falls through to the normal default rather
  // than rendering an empty screen.
  const [mode, setMode] = useState(() => urlTab());
  // Delivered by day has two views: the customers by delivery date, and the follow-ups with a
  // 72-hour deadline to the ones who never answered (Greg, 2026-10-27; components/DeliveredFollowup).
  const [deliveredView, setDeliveredView] = useState('customers');
  // See urlTab: only the first mount in a document may restore a pill.
  useEffect(() => { firstMount = false; }, []);

  // VIP outreach: the logged-in rep (signature + From), who's already been
  // emailed, and the customer currently open in the email modal.
  const [rep, setRep] = useState(() => {
    const u = getCurrentUser();
    const name = u?.staffName || 'The Crab Place Team';
    return { adminId: u?.id, name, email: '', title: titleForName(name) };
  });
  const [emailedMap, setEmailedMap] = useState({});
  const [outreachCustomer, setOutreachCustomer] = useState(null);
  const [draftRun, setDraftRun] = useState(null);   // {key, done, total} while a batch runs
  const [draftResult, setDraftResult] = useState(null);
  // Shipping tab: box-insert follow-up done state, keyed by ORDER number.
  const [insertMap, setInsertMap] = useState({});
  // Delegation: who the day's invites are handed to, who can be handed them,
  // and the per-rep sent/replied scoreboard for the range.
  const [assignMap, setAssignMap] = useState({});
  const [assignees, setAssignees] = useState([]);
  const [scoreboard, setScoreboard] = useState([]);
  const [assignPanel, setAssignPanel] = useState(null); // category key, or null
  const [mineOnly, setMineOnly] = useState(false);
  // ⚠️ "MINE ONLY" AND "GO TO THEM" ARE NOT THE SAME QUESTION, AND THAT IS THE BUG.
  // Mine only deliberately KEEPS UNASSIGNED customers visible - nobody owns them, so
  // anyone can pick one up. That is right for browsing and wrong the moment you click
  // a button that says "20 past due, 16 pending, go to them": you land on a range
  // where the unassigned are the overwhelming majority and it reads as a list of
  // other people's work. Rachael, 2026-09-01: "the screen it takes you to doesn't
  // just show you your assigned VIPs. It shows you the ones that haven't been
  // assigned to anyone yet. I'm under the mine only tool as well."
  // So the button now pins the EXACT customer ids it counted, and the list shows
  // those and nothing else until it is cleared.
  const [focusIds, setFocusIds] = useState(null);
  // What this rep still owes ACROSS EVERY DATE, not just the range on screen.
  // Refetched whenever the loaded data changes, so sending one closes it out here too.
  const [myOpen, setMyOpen] = useState(null);
  const isDelegator = useMemo(() => canDelegate(getCurrentUser()), []);

  const refreshMyOpen = useCallback(() => {
    const id = getCurrentUser()?.id;
    if (!id) return;
    // Never throws by design: the badge is a convenience, not the page.
    fetchMyOpenOutreach(id).then(setMyOpen);
  }, []);

  // Take me to my outstanding invitations, wherever they live. Sets the range wide
  // enough to hold every one of them and turns on Mine only, which is the whole ask:
  // "take us right to them rather than having to change the date range".
  const goToMyOpen = useCallback(() => {
    if (!myOpen || !myOpen.firstDate) return;
    setStart(String(myOpen.firstDate).slice(0, 10));
    setEnd(String(myOpen.lastDate || myOpen.firstDate).slice(0, 10));
    setMineOnly(true);
    // The rows the count was built from, so what lands on screen is what the
    // button said - not "mine, plus everything nobody has claimed".
    const ids = (myOpen.rows || []).map((r) => String(r.customer_id)).filter(Boolean);
    setFocusIds(ids.length ? new Set(ids) : null);
  }, [myOpen]);

  // Any deliberate move off that view drops the pin. Changing the dates by hand or
  // turning Mine only off both mean "show me something else", and a filter that
  // survives them is a filter people cannot get out of.
  const clearFocus = useCallback(() => setFocusIds(null), []);

  useEffect(() => {
    // ⚠️ The goal board reads none of this and daily_summary is an expensive query - it already
    // runs once server-side inside the board's own call, so fetching it here as well would pay
    // for the same work twice on every visit to the tab.
    // The notes tab has its own queries (NoteFollowupPanel) and reads none of daily_summary.
    if (mode === 'goal' || mode === 'notes' || mode === 'signups') { setLoading(false); return; }
    // Guard against an inverted range (end before start).
    const [lo, hi] = start <= end ? [start, end] : [end, start];
    setLoading(true);
    setError(null);
    fetchDailySummary(lo, hi, mode)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [start, end, mode]);

  // Resolve the rep's real name + email once (the session only has staffName).
  useEffect(() => {
    const u = getCurrentUser();
    if (!u?.id) return;
    fetchVipOutreachRep(u.id).then((info) => {
      if (info && (info.email || info.staff_name)) {
        setRep((r) => ({
          ...r,
          email: info.email || r.email,
          name: info.staff_name || r.name,
          title: titleForName(info.staff_name || r.name),
        }));
      }
    });
  }, []);

  // Which loaded non-VIP customers have already been emailed (drives the
  // emailed/waiting counts + row state). Refetches when the loaded data changes.
  useEffect(() => {
    if (!data) { setEmailedMap({}); return; }
    const ids = [];
    ['ex_vip', 'repeat_never_vip', 'new'].forEach((k) => {
      (data.categories[k]?.customers || []).forEach((c) => { if (c.id != null) ids.push(String(c.id)); });
    });
    if (!ids.length) { setEmailedMap({}); return; }
    let alive = true;
    fetchVipOutreachStatus(ids).then((m) => { if (alive) setEmailedMap(m || {}); });
    return () => { alive = false; };
  }, [data]);

  // Who can be handed work. Read live from adm_users, so a new customer-service
  // hire appears in the picker without a deploy.
  useEffect(() => { fetchOutreachAssignees().then(setAssignees); }, []);

  // Current assignment for every non-VIP customer on screen.
  const reloadAssignments = React.useCallback(() => {
    if (!data) { setAssignMap({}); return; }
    const ids = [];
    ['ex_vip', 'repeat_never_vip', 'new'].forEach((k) => {
      (data.categories[k]?.customers || []).forEach((c) => { if (c.id != null) ids.push(String(c.id)); });
    });
    if (!ids.length) { setAssignMap({}); return; }
    fetchOutreachAssignments(ids).then(setAssignMap);
  }, [data]);
  useEffect(() => { reloadAssignments(); }, [reloadAssignments]);

  // Per-rep sent/replied for the range. Counted by SEND date, which is not the
  // same axis as the cards above (they are by order or delivery date) — the
  // strip says so rather than letting the two numbers look like one number.
  useEffect(() => {
    if (!data || mode === 'shipping') { setScoreboard([]); return; }
    let alive = true;
    fetchOutreachScoreboard(data.start, data.end).then((r) => { if (alive) setScoreboard(r); });
    return () => { alive = false; };
  }, [data, mode]);
  // Re-read what I still owe every time the page reloads, so sending the last one
  // clears the badge rather than leaving it claiming work that is finished.
  useEffect(() => { refreshMyOpen(); }, [data, emailedMap, refreshMyOpen]);
  const markEmailed = (id, info) => setEmailedMap((m) => ({ ...m, [String(id)]: info }));

  // ⚠️ THE WHOLE PILL'S QUEUE INTO HER OUTLOOK DRAFTS, IN ONE PRESS. Greg, 2026-09-21: "you
  // can build the same tool for the delivered by date pill as well, and that one won't be
  // capped at 50." Replaces the .eml download, which the new Outlook swallows into a hidden
  // Outbox and never sends - the cause of the invites nobody could account for.
  //
  // ⚠️ IT REUSES buildOutreachEmail AND THE MODAL'S OWN CLAIM PAYLOAD. A second copy of either
  // would drift from what the composer sends, and the two are meant to be the same email.
  //
  // ⚠️ THE ORDER NOTE STILL GETS WRITTEN, one per drafted customer, fire-and-forget exactly as
  // the single send does. Rachael asked for that note in August and it is read in the live
  // admin; a bulk path that quietly skipped it would look like the feature had been removed.
  const draftAll = useCallback(async (key, list) => {
    setDraftResult(null);
    setDraftRun({ key, done: 0, total: list.length });
    try {
      const prepared = list.map((c) => {
        const built = buildOutreachEmail({ ...c, category: key }, rep, today);
        return { c, built };
      });
      const res = await draftIntoOutlook(prepared.map(({ c, built }) => ({
        key: c.id,
        claim: (track) => logVipOutreach({
          customerId: c.id, email: built.to, orderNumber: built.latestOrder || null,
          wouldHaveSaved: built.offer.saved, offerPrice: built.offer.price,
          adminId: rep.adminId, repName: rep.name, repEmail: rep.email,
          rangeStart: data?.start, rangeEnd: data?.end, mode,
          category: key,
          sendToken: track.token, pixelEmbedded: track.embedded,
        }),
        build: () => ({ subject: built.subject, html: built.html }),
      })), { onProgress: (done, total) => setDraftRun({ key, done, total }) });

      // Mark them on screen, and put the note on each order, only for the ones that really
      // were drafted - a blocked claim must leave no trace anywhere.
      const blockedIds = new Set((res.blocked || []).map((b) => String(b.key)));
      for (const { c, built } of prepared) {
        if (blockedIds.has(String(c.id))) continue;
        markEmailed(c.id, { sentAt: new Date().toISOString(), repName: rep.name });
        const internalId = built.latestOrderInternalId;
        if (!internalId) continue;
        const text = built.offer.free
          ? `Offered VIP membership free (would have saved ${money(built.offer.saved)})`
          : `Offered VIP membership for ${money(built.offer.price)}`;
        addOrderNote(internalId, text, rep.name).catch(() => {});
      }
      setDraftResult({ key, ...res });
    } catch (e) {
      setDraftResult({ key, error: e.message });
    } finally { setDraftRun(null); }
  }, [rep, today, data, mode, markEmailed]);
  // ⚠️ UNDO NO LONGER ERASES THE SEND, IT TOMBSTONES IT, and past a short grace it does not
  // re-open the customer. The .eml is out of our hands the moment it downloads, so an undo an
  // hour later cannot prove the email never went - and taking its word for it is how 50 people
  // got the win-back twice on 2026-09-16. The server decides; this only reports what it said.
  const handleUndoEmailed = (id) => {
    setEmailedMap((m) => { const n = { ...m }; delete n[String(id)]; return n; });
    undoVipOutreach(String(id), rep && rep.name).then((r) => {
      if (r && r.undone && r.cleared === false) {
        window.alert('Taken off your list. The send is more than 15 minutes old, though, so we cannot '
          + 'tell whether it left Outlook - this customer will not be offered again. Tell Rachael if it '
          + 'genuinely never went.');
      }
    }).catch(() => {});
  };

  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const setBoth = (s, e) => { setFocusIds(null); setStart(s); setEnd(e); };
  const yesterday = shiftDays(today, -1);
  const [lmStart, lmEnd] = lastMonthRange(today);
  const [lwStart, lwEnd] = lastWeekTueSat(today);
  const isToday = start === today && end === today;
  const isYesterday = start === yesterday && end === yesterday;
  const isThisMonth = start === firstOfMonth(today) && end === today;
  const isLastMonth = start === lmStart && end === lmEnd;
  const isLast7 = start === shiftDays(today, -6) && end === today;
  const isLastWeek = start === lwStart && end === lwEnd;

  const shippingMode = mode === 'shipping';

  // Switching tabs snaps the date to whatever that tab is worked on: Shipping ->
  // Yesterday (they prep the day ahead), Delivered -> last week Tue–Sat (the same
  // range the page opens on, so coming back to this tab lands where you started),
  // Placed -> Today.
  const changeMode = (m) => {
    setMode(m);
    rememberTab(m);
    const [s, e] = rangeForTab(m, today);
    setBoth(s, e);
  };

  // Shipping tab: which shipment orders already have their box insert marked.
  useEffect(() => {
    if (!data || mode !== 'shipping') { setInsertMap({}); return; }
    const nums = [];
    ['ex_vip', 'repeat_never_vip', 'new'].forEach((k) => {
      (data.categories[k]?.customers || []).forEach((c) => {
        (c.orders || []).forEach((o) => { if (o.number != null) nums.push(String(o.number)); });
      });
    });
    if (!nums.length) { setInsertMap({}); return; }
    let alive = true;
    fetchShipInsertStatus(nums).then((m) => { if (alive) setInsertMap(m || {}); });
    return () => { alive = false; };
  }, [data, mode]);

  const doMarkInsert = (customer) => {
    const nums = (customer.orders || []).map((o) => String(o.number)).filter(Boolean);
    if (!nums.length) return;
    const info = { doneAt: new Date().toISOString(), repName: rep.name };
    setInsertMap((m) => { const n = { ...m }; nums.forEach((x) => { n[x] = info; }); return n; });
    markShipInsert({ orderNumbers: nums, customerId: customer.id, adminId: rep.adminId, repName: rep.name, shipDate: data?.start }).catch(() => {});
  };
  const doUndoInsert = (customer) => {
    const nums = (customer.orders || []).map((o) => String(o.number)).filter(Boolean);
    setInsertMap((m) => { const n = { ...m }; nums.forEach((x) => delete n[x]); return n; });
    undoShipInsert(nums).catch(() => {});
  };

  // Inclusive day count for the per-day signup rate. Derived from the range the
  // LOADED DATA covers (data.start/data.end), NOT the currently-selected range —
  // otherwise switching Today -> This Month divides the still-showing Today
  // totals by the new 21-day span and flashes a meaningless rate until the fetch
  // resolves. Noon anchors dodge DST edges.
  const rangeDays = (() => {
    if (!data || !data.start || !data.end) return 1;
    const ms = new Date(data.end + 'T12:00:00') - new Date(data.start + 'T12:00:00');
    return Math.max(1, Math.round(ms / 86400000) + 1);
  })();

  // The number Greg actually wants: every new VIP won in this range, however they
  // came in. Checkout signups are order-line based; CS wins have no order line at
  // all (a rep honored the offer by hand), so the two are counted separately and
  // only ever added together here for the headline.
  const t = data && data.totals;
  const vipCheckout = (t && t.signups) || 0;
  const vipCsWins = (t && t.wonBack) || 0;
  const vipTotal = vipCheckout + vipCsWins;

  // Per-day rate only means something across a multi-day range — on a single day
  // it would just restate the headline number.
  const vipPerDay = rangeDays > 1 && vipTotal > 0
    ? `${(vipTotal / rangeDays).toFixed(1)}/day`
    : null;
  const vipPct = t && t.orders > 0 && vipTotal > 0
    ? `${((vipTotal / t.orders) * 100).toFixed(1)}% of orders`
    : null;

  // Win backs come straight from the API, anchored on the FLIP date. They are
  // NOT gathered off the category cards: a customer credited this week may not
  // have ordered in this range at all (the order that triggered the email is
  // usually older), so collecting from the cards silently drops most of them.
  const wonBackCustomers = (data && data.wonBackList) || [];

  const inputStyle = {
    fontSize: 13,
    padding: '6px 10px',
    border: '1px solid var(--gray-300, #d0d5dd)',
    borderRadius: 6,
    fontFamily: 'inherit',
  };

  return (
    <div>
      <div className="breadcrumb">
        <span>Dashboard</span>
        <span> › </span>
        <span>Orders Summary</span>
      </div>
      <div className="page-header">Orders Summary</div>

      {/* Placed vs Delivered: which date drives the range. Delivered lets you
          follow up with customers after their order actually arrived. */}
      <div style={{ display: 'flex', gap: 8, margin: '6px 0 2px', flexWrap: 'wrap' }}>
        {[['goal', "Today's Goal"], ['placed', 'Placed by day'], ['delivered', 'Delivered by day'], ['shipping', 'Shipping'], ['notes', 'Handwritten Notes'], ['winback', 'VIP Win-back'], ['signups', 'VIP Sign-ups'], ['replies', 'Replies']].map(([m, label]) => (
          <button
            key={m}
            onClick={() => changeMode(m)}
            style={{
              fontSize: 13,
              fontWeight: 700,
              padding: '8px 18px',
              borderRadius: 8,
              cursor: 'pointer',
              fontFamily: 'inherit',
              border: `1px solid ${mode === m ? 'var(--navy)' : 'var(--gray-300, #d0d5dd)'}`,
              background: mode === m ? 'var(--navy)' : '#fff',
              color: mode === m ? '#fff' : 'var(--navy)',
              boxShadow: mode === m ? '0 1px 3px rgba(0,57,92,0.25)' : '0 1px 2px rgba(16,24,40,0.05)',
            }}
          >{label}</button>
        ))}
      </div>
      {/* Win-back is a COHORT, not a date range, so it replaces everything below the pills
          rather than filtering it. Returning early here leaves the date machinery untouched. */}
      {/* The morning board. Like win-back it is NOT a date range, so it replaces everything below
          the pills rather than filtering it - returning early leaves the date machinery alone. */}
      {mode === 'goal' && (
        <>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', margin: '6px 0 14px' }}>
            Where we are against 7,000 renewing members, what needs doing today, and how yesterday went.
          </div>
          <VipGoalBoard />
        </>
      )}

      {/* What came back. Like the goal board and win-back it is a COHORT rather than a date
          range, so it replaces everything below the pills. Renders NOTHING for anybody whose
          account is not on the allowlist, so a colleague sees an empty tab rather than a
          refusal - it carries customer mail from three people's mailboxes. */}
      {mode === 'replies' && (
        <>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', margin: '6px 0 14px' }}>
            Replies to the VIP invites, win-back and Delivered by day. Click a row to read it.
          </div>
          <OutreachRepliesPanel />
        </>
      )}

      {mode === 'signups' && (
        <>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', margin: '6px 0 14px' }}>
            Everyone who joined after one of our offers, newest first, with the term they were given.
            A red row is one whose term does not match its campaign, or that is set not to renew.
            Click a red "to check" count to see just those, and a name to open the customer.
          </div>
          <VipSignupsPanel navigateTo={navigateTo} />
        </>
      )}

      {mode === 'notes' && (
        <>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', margin: '6px 0 14px' }}>
            The handwritten note in every non-VIP box. Mark who packed it on the packing list; that rep
            follows up a few days after delivery, and a morning email lists who is due.
          </div>
          <NoteFollowupPanel rep={rep} />
        </>
      )}

      {mode === 'winback' && (
        <>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', margin: '6px 0 14px' }}>
            Lapsed VIP members, grouped by the year they fell off and why. Start with the ones whose
            card failed in April: they did not choose to leave.
          </div>
          <VipWinbackPanel rep={rep} />
        </>
      )}

      {mode === 'delivered' && (
        <div style={{ display: 'flex', gap: 6, margin: '10px 0 4px', flexWrap: 'wrap' }}>
          {[['customers', 'Customers by delivery date'], ['followup', 'Follow-ups (72-hour deadline)']].map(([v, label]) => (
            <button key={v} onClick={() => setDeliveredView(v)}
              style={{ fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                border: '1px solid ' + (deliveredView === v ? 'var(--orange)' : 'var(--gray-300, #d0d5dd)'),
                background: deliveredView === v ? '#fff3ed' : '#fff', color: deliveredView === v ? 'var(--orange)' : 'var(--gray-700)' }}>
              {label}</button>
          ))}
        </div>
      )}

      {mode === 'delivered' && deliveredView === 'followup' && <DeliveredFollowup rep={rep} />}

      {mode !== 'winback' && mode !== 'goal' && mode !== 'replies' && mode !== 'notes' && mode !== 'signups' && !(mode === 'delivered' && deliveredView === 'followup') && (<>

      {/* Rachael, 2026-09-16: "are these going to junk?" Collapsed by default. */}
      <div style={{ marginTop: 10 }}>
        <OutreachDeliverability kind="invite" title="Are the VIP invites landing?" />
      </div>

      <div style={{ fontSize: 12, color: 'var(--gray-500)', margin: '2px 0 0' }}>
        {shippingMode
          ? 'Orders SHIPPING on the selected day (by ship date). Mark the box insert done as you write it up and put it in the box.'
          : mode === 'delivered'
          ? 'Range = orders DELIVERED in these dates (by actual delivery date). Orders still in transit aren’t counted yet.'
          : 'Range = orders PLACED in these dates (by order date).'}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0 8px', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>From</label>
        <input type="date" value={start} max={today} onChange={(e) => { if (e.target.value) { clearFocus(); setStart(e.target.value); } }} style={inputStyle} />
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>To</label>
        <input type="date" value={end} max={today} onChange={(e) => { if (e.target.value) { clearFocus(); setEnd(e.target.value); } }} style={inputStyle} />

        <div style={{ display: 'flex', gap: 6, marginLeft: 4 }}>
          <QuickPick active={isToday} onClick={() => setBoth(today, today)}>Today</QuickPick>
          <QuickPick active={isYesterday} onClick={() => setBoth(yesterday, yesterday)}>Yesterday</QuickPick>
          {!shippingMode && (
            <>
              <QuickPick active={isLast7} onClick={() => setBoth(shiftDays(today, -6), today)}>Last 7 Days</QuickPick>
              <QuickPick active={isLastWeek} onClick={() => setBoth(lwStart, lwEnd)}>Last Week (Tue–Sat)</QuickPick>
              <QuickPick active={isThisMonth} onClick={() => setBoth(firstOfMonth(today), today)}>This Month</QuickPick>
              <QuickPick active={isLastMonth} onClick={() => setBoth(lmStart, lmEnd)}>Last Month</QuickPick>
            </>
          )}
        </div>

        {loading && <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading…</span>}
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 18 }}>
        {rangeLabel(start <= end ? start : end, start <= end ? end : start)}
      </div>

      {error && (
        <div style={{ padding: 16, fontSize: 13, color: '#c0392b', background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6 }}>
          <strong>Error loading Orders Summary:</strong> {error}
        </div>
      )}

      {!error && data && (
        <>
          {/* Overall totals — all orders across the three buckets */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 18px',
              marginBottom: 14,
              borderRadius: 10,
              background: 'var(--navy)',
              color: '#fff',
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700 }}>All Orders</span>
            <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.8 }}>
              {data.totals.customerCount} {data.totals.customerCount === 1 ? 'customer' : 'customers'}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 18 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 120px 90px', alignItems: 'center', columnGap: 18 }}>
                <Stat label="Orders" value={<>{data.totals.orders}<ReshipSup n={data.totals.reships} light /></>} light />
                <Stat label="Total Rev" value={money(data.totals.revenue)} light accent />
                <Stat label="AOV" value={money(data.totals.aov)} light />
              </div>

              {/* New VIPs — the headline. Both routes added together, because "we got
                  4 today" is the win; the breakout underneath says where they came from. */}
              <div
                title="Every new VIP in this range: checkout signups plus customer-service wins"
                style={{
                  minWidth: 176,
                  padding: '9px 14px',
                  borderRadius: 8,
                  textAlign: 'right',
                  background: 'rgba(255,255,255,0.10)',
                  border: '1px solid rgba(255,255,255,0.22)',
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'rgba(255,255,255,0.75)' }}>
                  New VIPs
                </div>
                <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, color: vipTotal > 0 ? 'var(--orange-light)' : '#fff' }}>
                  {vipTotal > 0 ? `+${vipTotal}` : '0'}
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
                  {vipCheckout} checkout · {vipCsWins} CS {vipCsWins === 1 ? 'win' : 'wins'}
                </div>
                {(vipPerDay || vipPct) && (
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginTop: 1 }}>
                    {[vipPerDay, vipPct].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {mode !== 'shipping' && (
            <OutreachScoreboard
              rows={scoreboard}
              assignMap={assignMap}
              rangeText={rangeLabel(data.start, data.end)}
              mineOnly={mineOnly}
              onToggleMine={() => { clearFocus(); setMineOnly((v) => !v); }}
              repName={rep.name}
              myOpen={myOpen}
              onGoToMyOpen={goToMyOpen}
              focusCount={focusIds ? focusIds.size : 0}
              onClearFocus={clearFocus}
            />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {CATEGORY_ORDER.map(({ key, showSignups, historyDetail }) => (
              <CategoryCard
                key={key}
                cat={data.categories[key]}
                expanded={!!open[key]}
                onToggle={() => toggle(key)}
                showSignups={showSignups}
                historyDetail={historyDetail}
                totalOrders={data.totals.orders}
                dateFrom={data.start}
                dateTo={data.end}
                modeLabel={mode}
                emailedMap={emailedMap}
                onEmail={(cust) => setOutreachCustomer({ ...cust, category: key })}
                drafting={draftRun && draftRun.key === key ? draftRun : null}
                draftResult={draftResult && draftResult.key === key ? draftResult : null}
                onDismissDraft={() => setDraftResult(null)}
                onDraftAll={(list) => draftAll(key, list)}
                onUndoEmailed={handleUndoEmailed}
                insertMap={insertMap}
                onMarkInsert={doMarkInsert}
                onUndoInsert={doUndoInsert}
                assignMap={assignMap}
                isDelegator={isDelegator}
                onOpenAssign={() => setAssignPanel(key)}
                mineOnly={mineOnly}
                myName={rep.name}
                myAdminId={rep.adminId}
                focusIds={focusIds}
              />
            ))}
            <WonBackCard
              customers={wonBackCustomers}
              expanded={!!open.won_back}
              onToggle={() => toggle('won_back')}
              rangeText={rangeLabel(data.start, data.end)}
            />
          </div>
        </>
      )}

      {assignPanel && data && (
        <AssignPanel
          cat={data.categories[assignPanel]}
          categoryKey={assignPanel}
          assignees={assignees}
          assignMap={assignMap}
          emailedMap={emailedMap}
          workDate={data.start}
          mode={mode}
          assignedByAdminId={rep.adminId}
          isDelegator={isDelegator}
          onClose={() => setAssignPanel(null)}
          onChanged={reloadAssignments}
        />
      )}
      {outreachCustomer && (
        <OutreachModal
          customer={outreachCustomer}
          rep={rep}
          todayIso={today}
          alreadyEmailed={emailedMap[String(outreachCustomer.id)]}
          rangeStart={data?.start}
          rangeEnd={data?.end}
          mode={mode}
          onClose={() => setOutreachCustomer(null)}
          onSent={(id, info) => markEmailed(id, info)}
        />
      )}
      </>)}
    </div>
  );
}

function QuickPick({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: '6px 12px',
        borderRadius: 999,
        cursor: 'pointer',
        border: `1px solid ${active ? 'var(--orange)' : 'var(--gray-300, #d0d5dd)'}`,
        background: active ? 'var(--orange)' : '#fff',
        color: active ? '#fff' : 'var(--navy)',
      }}
    >
      {children}
    </button>
  );
}

// Client-side CSV export of one segment's customers for the loaded range/mode.
// Uses the data already in memory (no extra fetch) so it always matches what's
// on screen. One row per customer with their in-range + lifetime numbers.
function exportCategoryCsv(cat, from, to, modeLabel) {
  const esc = (v) => { if (v == null) return ''; const s = String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
  const d = (v) => { const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[1]}-${m[2]}-${m[3]}` : ''; };
  const headers = ['Customer', 'Email', 'VIP', 'Signed Up In Range', 'Orders In Range', 'Revenue In Range', 'Order #s', 'Channel', 'Lifetime Orders', 'Lifetime Revenue', 'Orders 12mo', 'AOV', 'Would’ve Saved (range)', 'First Order', 'Last Order'];
  const rows = (cat.customers || []).map((c) => [
    c.name, c.email, c.isVip ? 'Yes' : 'No', c.signedUpToday ? 'Yes' : '',
    c.todayOrders, Number(c.todayRev || 0).toFixed(2),
    (c.orders || []).map((o) => o.number).join(' '), c.channel || '',
    c.lifetimeOrders, Number(c.lifetimeRev || 0).toFixed(2), c.orders12mo,
    Number(c.aov || 0).toFixed(2), Number(c.whsPeriod || 0).toFixed(2),
    d(c.firstOrder), d(c.lastOrder),
  ].map(esc).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const slug = (cat.label || 'segment').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  a.href = url;
  a.download = `orders-summary_${slug}_${modeLabel === 'delivered' ? 'delivered' : modeLabel === 'shipping' ? 'shipping' : 'placed'}_${from}_to_${to}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function CategoryCard({ cat, expanded, onToggle, showSignups, historyDetail, totalOrders, dateFrom, dateTo, modeLabel, emailedMap = {}, onEmail, onUndoEmailed, insertMap = {}, onMarkInsert, onUndoInsert, assignMap = {}, isDelegator = false, onOpenAssign, mineOnly = false, myName = '', myAdminId = null, focusIds = null, onDraftAll, drafting = null, draftResult = null, onDismissDraft }) {
  const isVipCat = cat && cat.key === 'existing_vip';
  const showChannel = cat && cat.key === 'new';
  // "Would've saved" is a non-VIP outreach metric — existing VIPs already get it.
  const showWhs = cat && cat.key !== 'existing_vip';
  // Both actions (the VIP-invite email, and the Shipping-tab box insert) apply to
  // the non-VIP buckets only — existing VIPs are already members.
  const outreachEnabled = cat && cat.key !== 'existing_vip';
  const shippingMode = modeLabel === 'shipping';
  // "Done" = insert marked (Shipping tab) or emailed (Placed/Delivered tabs).
  const isDone = (c) => shippingMode
    ? (Array.isArray(c.orders) && c.orders.length > 0 && c.orders.every((o) => !!insertMap[String(o.number)]))
    : !!emailedMap[String(c.id)];
  // Per-card sort state; defaults to Revenue desc (the server's default order).
  const [sort, setSort] = useState({ key: 'todayRev', dir: 'desc' });

  if (!cat) return null;

  // Somebody who joined the VIP Club in this window has nothing left to be
  // invited to, so they get no button and are not counted as waiting. (Shipping
  // mode's action is the physical box insert, which is a different job — left as is.)
  // Nobody who is already in the club gets invited to join it. Two ways that
  // happens: they bought the membership inside this range (signedUpToday), or
  // they joined at some point between the range and today (currentlyVip) —
  // which is the one that reached a customer. The category is left alone on
  // purpose: it is the true record of what they were when they ordered, and
  // rewriting it would corrupt every historical view of that day.
  // ⚠️ A HANDWRITTEN NOTE FROM ANOTHER REP TAKES THE CUSTOMER OFF THIS LIST'S OFFER (Rachael's
  // conflict rule, 2026-09-30). The note rep's own rows stay offerable - one person, one message.
  const noteOwnedByOther = (c) => !shippingMode && !!c.noteRep
    && !(myAdminId != null && String(c.noteRepAdminId) === String(myAdminId));
  const noOfferNeeded = (c) => !shippingMode && (!!c.signedUpToday || !!c.currentlyVip || !!c.doNotContact || !!c.hasChargeback || noteOwnedByOther(c));
  const doneCount = outreachEnabled ? cat.customers.filter(isDone).length : 0;
  const waitingCount = outreachEnabled
    ? cat.customers.filter((c) => !isDone(c) && !noOfferNeeded(c)).length
    : cat.customerCount - doneCount;
  // Who sent the ones that went out, over the customers ACTUALLY ON SCREEN, so
  // the split can never disagree with the rows underneath it.
  const repSplit = [];
  if (outreachEnabled && !shippingMode) {
    const tally = new Map();
    cat.customers.forEach((c) => {
      const info = emailedMap[String(c.id)];
      if (!info) return;
      const name = String(info.repName || 'Unattributed').split(' ')[0];
      tally.set(name, (tally.get(name) || 0) + 1);
    });
    [...tally.entries()].sort((x, y) => y[1] - x[1]).forEach(([n, v]) => repSplit.push([n, v]));
  }

  // Column config drives BOTH the header cells and the row cells so they can't
  // drift out of sync, and gives each column a sort type. `whsKey` hangs a green
  // "would've saved as a VIP" sub-value under the matching order-count column.
  const columns = [
    { key: 'name', label: 'Customer', type: 'str', align: 'left' },
    { key: 'todayOrders', label: 'Orders', type: 'num', align: 'right', whsKey: 'whsPeriod' },
    { key: 'todayRev', label: 'Revenue', type: 'num', align: 'right', accent: true, fmt: money },
    ...(historyDetail ? [
      { key: 'lifetimeOrders', label: 'Orders (All-Time)', type: 'num', align: 'right', whsKey: 'whsAllTime' },
      { key: 'orders12mo', label: 'Orders (12 mo)', type: 'num', align: 'right', whsKey: 'whs12mo' },
      { key: 'lastOrder', label: 'Last Order', type: 'date', align: 'right', fmt: fmtDate },
      { key: 'aov', label: 'AOV / Customer', type: 'num', align: 'right', fmt: money },
    ] : []),
    ...(showChannel ? [{ key: 'channel', label: 'Attribution', type: 'str', align: 'right' }] : []),
  ];

  const onSort = (col) => {
    setSort((s) =>
      s.key === col.key
        ? { key: col.key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, dir: col.type === 'str' ? 'asc' : 'desc' }
    );
  };

  const sortCol = columns.find((c) => c.key === sort.key);
  // ⚠️ SORTED LAST, AFTER WHATEVER COLUMN SORT IS ACTIVE. Rachael, 2026-09-01: "is
  // there anyway we can have the customers who signed up for the VIP on their own
  // moved to the bottom rather than be in the middle of the list?" They joined
  // without anybody emailing them, so there is nothing to do on the row - it is a
  // result, not a job, and sitting mid-list it just interrupts the scan for the
  // ones that still need an invitation. A CS win is NOT this: somebody worked for
  // that one, so it stays where the sort puts it.
  const joinedOnTheirOwn = (c) => !!c.signedUpToday && !c.wonBack;
  // "Mine only" hides what is assigned to somebody else. An UNASSIGNED customer
  // stays visible on purpose — nobody owns it yet, so anyone can pick it up.
  const myFirst = String(myName || '').split(' ')[0].toLowerCase();
  const mineFiltered = (mineOnly && outreachEnabled && !shippingMode)
    ? cat.customers.filter((c) => {
        const as = assignMap[String(c.id)];
        if (!as || !as.assigneeName) return true;
        return String(as.assigneeName).split(' ')[0].toLowerCase() === myFirst;
      })
    : cat.customers;
  // ⚠️ THE PINNED SET WINS OVER "MINE ONLY", because it answers a narrower question:
  // not "whose is this" but "which ones did that button just count". Everything else
  // is hidden, including unassigned rows that Mine only would have let through.
  const focused = (focusIds && focusIds.size && outreachEnabled && !shippingMode)
    ? cat.customers.filter((c) => focusIds.has(String(c.id)))
    : mineFiltered;
  // Do not contact rows are left off the outreach list entirely (Rachael, Nov 2, 2026: "Can we
  // have customers on the do not contact list excluded from the delivered by day report?").
  const rows = focused.filter((c) => !(outreachEnabled && !shippingMode && c.doNotContact));
  if (sortCol) {
    const dir = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let av = a[sortCol.key], bv = b[sortCol.key];
      if (sortCol.type === 'num') return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
      if (sortCol.type === 'date') {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
        return (av - bv) * dir;
      }
      av = (av || '').toString().toLowerCase();
      bv = (bv || '').toString().toLowerCase();
      return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
    });
  }
  // Push already-done customers (emailed, or insert-marked) to the bottom so reps
  // work the untouched ones first (stable sort keeps the column order per group).
  if (outreachEnabled) {
    rows.sort((a, b) => (isDone(a) ? 1 : 0) - (isDone(b) ? 1 : 0));
    // ...and below even those, the ones who joined on their own. Applied LAST so it
    // is the outermost grouping: nothing on those rows is actionable, so they belong
    // under the work rather than through the middle of it. Stable, so the column
    // sort still holds inside each group.
    rows.sort((a, b) => (joinedOnTheirOwn(a) ? 1 : 0) - (joinedOnTheirOwn(b) ? 1 : 0));
  }

  return (
    <div style={{ border: '1px solid var(--gray-200, #e4e7ec)', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
      {/* Header — summary lives here */}
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 16px',
          background: expanded ? 'var(--gray-50, #f9fafb)' : '#fff',
          border: 'none',
          borderBottom: expanded ? '1px solid var(--gray-200, #e4e7ec)' : 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {expanded ? <ChevronDown size={18} color="var(--gray-500)" /> : <ChevronRight size={18} color="var(--gray-500)" />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 250 }}>
          {isVipCat && <Crown size={16} color="var(--orange)" />}
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>{cat.label}</span>
          {/* Rachael asked whether this number was the people signed up or the people
              to contact. It is neither on its own: it is everybody in this block for
              the dates on screen, and the emailed/waiting chip beside it is the split
              within that. Said out loud here so nobody has to ask again. */}
          <span
            title={`Every customer in this block for the dates shown, whether or not anyone has emailed them yet. The emailed and waiting counts beside it are the split within this ${cat.customerCount}.`}
            style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', cursor: 'help', borderBottom: '1px dotted var(--gray-300, #d0d5dd)' }}
          >
            {cat.customerCount} {cat.customerCount === 1 ? 'customer' : 'customers'}
          </span>
          {cat.customerCount > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); exportCategoryCsv(cat, dateFrom, dateTo, modeLabel); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); exportCategoryCsv(cat, dateFrom, dateTo, modeLabel); } }}
              title={`Export these ${cat.customerCount} customers to CSV (current date range)`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--orange)', border: '1px solid var(--orange)', borderRadius: 999, padding: '2px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              <Download size={12} /> Export CSV
            </span>
          )}
          {outreachEnabled && cat.customerCount > 0 && (
            <span
              title={shippingMode ? 'Box-insert progress for the customers shipping in this range' : 'VIP outreach progress for the customers in this list'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              <span style={{ color: '#0b8043' }}>{doneCount} {shippingMode ? 'insert done' : 'emailed'}</span>
              <span style={{ color: 'var(--gray-300, #d0d5dd)' }}>·</span>
              <span style={{ color: 'var(--orange)' }}>{waitingCount} waiting</span>
            </span>
          )}
          {repSplit.length > 0 && (
            <span
              title="Who sent the invitations in this block"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}
            >
              {repSplit.map(([n, v], i) => (
                <span key={n}>
                  {i > 0 && <span style={{ color: 'var(--gray-300, #d0d5dd)', marginRight: 6 }}>·</span>}
                  {n} <strong style={{ color: 'var(--navy)' }}>{v}</strong>
                </span>
              ))}
            </span>
          )}
          {isDelegator && outreachEnabled && !shippingMode && cat.customerCount > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onOpenAssign && onOpenAssign(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onOpenAssign && onOpenAssign(); } }}
              title="Hand these customers out with a deadline"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: '#fff', background: 'var(--navy)', borderRadius: 999, padding: '3px 11px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              <Users size={12} /> Assign
            </span>
          )}
        </div>

        <div
          style={{
            marginLeft: 'auto',
            display: 'grid',
            gridTemplateColumns: '80px 120px 90px 96px',
            alignItems: 'center',
            columnGap: 18,
          }}
        >
          <Stat
            label="Orders"
            value={cat.orders}
            sub={totalOrders > 0 && cat.orders > 0
              ? `${((cat.orders / totalOrders) * 100).toFixed(1)}% of total`
              : null}
          />
          <Stat label="Total Rev" value={money(cat.revenue)} accent />
          <Stat label="AOV" value={money(cat.aov)} />
          {showSignups ? (
            <Stat
              label="VIP Signups"
              value={cat.signups > 0 ? `+${cat.signups}` : '0'}
              accent={cat.signups > 0}
              sub={cat.orders > 0 && cat.signups > 0
                ? `${((cat.signups / cat.orders) * 100).toFixed(1)}% of orders`
                : null}
            />
          ) : (
            <span aria-hidden="true" />
          )}
        </div>
      </button>

      {expanded && outreachEnabled && onDraftAll && (() => {
        // ⚠️ THE WHOLE WAITING QUEUE INTO HER OUTLOOK DRAFTS. Uncapped here, by Greg's call on
        // 2026-09-21 - a delivered-by-day cohort is the week's work, not a daily 50.
        // Only people genuinely still waiting AND with an address: the same test the per-row
        // button uses, so the count on this button and the buttons in the rows cannot disagree.
          // ⚠️ THE BUTTON TAKES ONLY WHAT IS HERS, NEVER A COLLEAGUE'S ASSIGNMENT. Ashley,
          // 2026-09-28/29: Rachael dealt her 4 in a category, the button offered 8, and after one
          // rep pressed it the customers Rachael had given Ashley "disappeared" - claimed into
          // somebody else's Outlook. It had counted the whole waiting category and ignored the
          // assignment the "Mine only" filter above already respects.
          //   - assigned to somebody else -> never included
          //   - she has assignments in this category -> exactly those
          //   - she has none here -> the unassigned pool, as it worked before assignment existed
          const firstOf = (n) => String(n || '').split(' ')[0].toLowerCase();
          const isMine = (as) => (myAdminId != null && as.assigneeAdminId != null
            && String(as.assigneeAdminId) === String(myAdminId))
            || (!!as.assigneeName && firstOf(as.assigneeName) === firstOf(myName));
          const waiting = cat.customers.filter((c) => !isDone(c) && !noOfferNeeded(c) && c.email);
          const mineHere = waiting.filter((c) => { const as = assignMap[String(c.id)]; return as && as.assigneeName && isMine(as); });
          const eligible = mineHere.length
            ? mineHere
            : waiting.filter((c) => { const as = assignMap[String(c.id)]; return !as || !as.assigneeName; });
          if (!eligible.length && !draftResult) return null;
          return (
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--gray-200)', background: '#f7fafc' }}>
              {eligible.length > 0 && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => onDraftAll(eligible)}
                    disabled={!!drafting}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700,
                      color: '#fff', background: 'var(--navy)', border: 'none', borderRadius: 6,
                      padding: '6px 12px', cursor: drafting ? 'default' : 'pointer', opacity: drafting ? 0.6 : 1,
                    }}
                  >
                    {drafting
                      ? `Writing ${drafting.done} of ${drafting.total}...`
                      : `Put ${eligible.length} in my Outlook drafts`}
                  </button>
                  <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>
                    Already written, waiting in Outlook. Read each one and press Send. Nothing is sent for you.
                  </span>
                </div>
              )}
              {draftResult && (
                <div style={{ marginTop: eligible.length ? 8 : 0, fontSize: 12.5, lineHeight: 1.6, color: draftResult.error ? '#c53030' : 'var(--navy)' }}>
                  {draftResult.error ? (
                    <b>Could not write the drafts: {draftResult.error}</b>
                  ) : (
                    <>
                      <b>{draftResult.created} {draftResult.created === 1 ? 'draft is' : 'drafts are'} in your Outlook
                        {draftResult.mailbox ? ' (' + draftResult.mailbox + ')' : ''}.</b>{' '}
                      Open Outlook, read each one and press Send.
                      {(draftResult.blocked || []).length > 0 && (
                        <span style={{ color: 'var(--gray-600)' }}> {draftResult.blocked.length} skipped.</span>
                      )}
                      {(draftResult.failed || []).length > 0 && (
                        <span style={{ color: '#c53030' }}> {draftResult.failed.length} could not be written - try those again.</span>
                      )}
                    </>
                  )}
                  <button type="button" onClick={onDismissDraft}
                    style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', marginLeft: 8 }}>
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          );
      })()}

      {/* Body — per-customer detail (click a header to sort) */}
      {expanded && (
        <div style={{ padding: cat.customers.length ? 0 : 16, overflowX: 'auto' }}>
          {cat.customers.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--gray-500)', textAlign: 'center' }}>
              No orders from this group in the selected range.
            </div>
          ) : (
            <>
            {showWhs && (
              <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-100, #f2f4f7)' }}>
                VIP savings (8% off product + free packing over $100 + VIP shipping rate), shown under this order, the last 12 months, and all-time.{' '}
                <span style={{ color: '#d92d20', fontWeight: 700 }}>Red “missed”</span> = didn’t join, left it on the table (go after these).{' '}
                <span style={{ color: '#0b8043', fontWeight: 700 }}>Green “saved”</span> = joined VIP, actually captured it.
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--gray-50, #f9fafb)', color: 'var(--gray-500)' }}>
                  {columns.map((col) => (
                    <SortableTh
                      key={col.key}
                      col={col}
                      active={sort.key === col.key}
                      dir={sort.dir}
                      onClick={() => onSort(col)}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const done = outreachEnabled && isDone(c);
                  return (
                  <tr key={c.id} style={{ borderTop: '1px solid var(--gray-100, #f2f4f7)', opacity: done ? 0.55 : 1 }}>
                    {columns.map((col) => {
                      if (col.key === 'name') {
                        return (
                          <td key={col.key} style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.name}</span>
                              {c.isVip && <Badge color="#dcb164" bg="#fbf3df">{c.vipNotRenewing ? 'VIP - NOT RENEWING' : 'VIP'}</Badge>}
                              {c.signedUpToday && (
                                <Badge color="#fff" bg="var(--orange)">
                                  <Sparkles size={10} style={{ marginRight: 3, verticalAlign: '-1px' }} />
                                  Joined VIP
                                </Badge>
                              )}
                              {c.currentlyVip && !c.signedUpToday && !c.wonBack && (
                                <Badge color="#fff" bg="#7a5af5">
                                  Already a VIP{c.currentlyVipSince ? ` since ${fmtDate(c.currentlyVipSince)}` : ''}
                                </Badge>
                              )}
                              {c.wonBack && !c.signedUpToday && (
                                <Badge color="#fff" bg="#0b8043">
                                  <Sparkles size={10} style={{ marginRight: 3, verticalAlign: '-1px' }} />
                                  CS Win
                                </Badge>
                              )}
                            </div>
                            {c.email && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{c.email}</div>}
                            {Array.isArray(c.orders) && c.orders.length > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 3 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, color: 'var(--gray-400, #98a2b3)' }}>
                                  {c.orders.length > 1 ? 'Orders' : 'Order'}
                                </span>
                                {c.orders.map((o) => (
                                  <OrderLink key={o.linkId} order={o} />
                                ))}
                              </div>
                            )}
                            {outreachEnabled && !shippingMode && c.doNotContact && (
                              <div style={{ marginTop: 5, fontSize: 11, fontWeight: 700, color: '#b42318' }}>Do not contact</div>
                            )}
                            {outreachEnabled && !shippingMode && c.hasChargeback && !c.doNotContact && (
                              <div style={{ marginTop: 5, fontSize: 11, fontWeight: 700, color: '#b42318' }} title="Filed a chargeback - no VIP offer">Chargeback</div>
                            )}
                            {outreachEnabled && noteOwnedByOther(c) && !done && !c.currentlyVip && !c.doNotContact && (
                              <div style={{ marginTop: 5, fontSize: 11, fontWeight: 700, color: 'var(--navy)' }}
                                title="This rep packed a handwritten note and follows up after delivery">
                                Note follow-up: {String(c.noteRep).split(' ')[0]}
                              </div>
                            )}
                            {outreachEnabled && !shippingMode && !noOfferNeeded(c) && assignMap[String(c.id)] && !done && (
                              <AssignChip a={assignMap[String(c.id)]} />
                            )}
                            {outreachEnabled && (done || !noOfferNeeded(c)) && (
                              <div style={{ marginTop: 5 }}>
                                {shippingMode ? (
                                  done ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#fff', background: '#0b8043', fontWeight: 700, borderRadius: 999, padding: '3px 10px' }}>
                                        <Check size={12} /> Insert done{insertDoneDate(c, insertMap) ? ` ${fmtDate(insertDoneDate(c, insertMap))}` : ''}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => onUndoInsert && onUndoInsert(c)}
                                        style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                                      >Undo</button>
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => onMarkInsert && onMarkInsert(c)}
                                      title="Mark the box insert as written up and going in the box"
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        fontSize: 11, fontWeight: 700, color: 'var(--orange)',
                                        background: '#fff', border: '1px solid var(--orange)',
                                        borderRadius: 999, padding: '4px 12px', cursor: 'pointer',
                                      }}
                                    >
                                      <Package size={12} /> Insert done
                                    </button>
                                  )
                                ) : (
                                  done ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#0b8043', fontWeight: 700 }}>
                                        <Check size={12} /> Emailed{emailedMap[String(c.id)]?.sentAt ? ` ${fmtDate(emailedMap[String(c.id)].sentAt)}` : ''}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => onUndoEmailed && onUndoEmailed(c.id)}
                                        style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                                      >Undo</button>
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => c.email && onEmail && onEmail(c)}
                                      disabled={!c.email}
                                      title={c.email ? 'Draft a VIP Club invitation email for this customer' : 'No email on file for this customer'}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        fontSize: 11, fontWeight: 700,
                                        color: c.email ? '#fff' : 'var(--gray-400, #98a2b3)',
                                        background: c.email ? 'var(--orange)' : 'var(--gray-100, #f2f4f7)',
                                        border: 'none', borderRadius: 6, padding: '4px 10px',
                                        cursor: c.email ? 'pointer' : 'not-allowed',
                                      }}
                                    >
                                      <Mail size={12} /> Email VIP offer
                                    </button>
                                  )
                                )}
                              </div>
                            )}
                          </td>
                        );
                      }
                      if (col.key === 'channel') {
                        return <td key={col.key} style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}><AttributionCell customer={c} /></td>;
                      }
                      const raw = c[col.key];
                      const whs = col.whsKey && showWhs ? Number(c[col.whsKey]) || 0 : 0;
                      // Joined VIP this range => they DID capture it (green "saved").
                      // Didn't join => they left it on the table (red "missed") — the
                      // outreach targets.
                      const captured = c.signedUpToday || c.wonBack;
                      return (
                        <Td key={col.key} right={col.align === 'right'} accent={col.accent}>
                          {col.fmt ? col.fmt(raw) : raw}
                          {whs > 0 && (
                            <div
                              style={{ fontSize: 11, fontWeight: 700, color: captured ? '#0b8043' : '#d92d20', marginTop: 2 }}
                              title={captured ? 'Saved as a VIP member' : 'Missed — would have saved as a VIP member'}
                            >
                              {captured ? 'saved' : 'missed'} {money(whs)}
                            </div>
                          )}
                        </Td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Attribution pill for the Brand-New bucket. order_attribution only has a row
// when the click was tagged, so an untagged/direct order shows a muted marker.
const CHANNEL_STYLE = {
  'Meta · Paid': { c: '#1877f2', bg: '#e7f0fe' },
  'Meta · Organic': { c: '#1877f2', bg: '#eef4fe' },
  'Google · Paid': { c: '#188038', bg: '#e6f4ea' },
  'Google · Paid (Brand)': { c: '#b54708', bg: '#fdf1e3' },
  'Organic Search': { c: '#0b8043', bg: '#e9f6ee' },
  'Email': { c: '#7b3fe4', bg: '#f0e9fd' },
  'YouTube': { c: '#c4302b', bg: '#fdecec' },
};
function ChannelPill({ channel }) {
  if (!channel) {
    return <span style={{ fontSize: 11, color: 'var(--gray-400, #98a2b3)', fontStyle: 'italic' }}>Direct / Untagged</span>;
  }
  const s = CHANNEL_STYLE[channel] || { c: 'var(--gray-600, #475467)', bg: 'var(--gray-100, #f2f4f7)' };
  return (
    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color: s.c, background: s.bg, padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {channel}
    </span>
  );
}

// Attribution cell for the Brand-New bucket. For Email-attributed customers it
// lazily fetches the Dotdigital newsletter-signup date and shows it under the
// pill. Module-level cache dedupes fetches across re-renders / re-sorts.
const ddDateCache = new Map(); // email -> {signupDate, found} | 'loading'
function AttributionCell({ customer }) {
  const isEmail = customer.channel === 'Email';
  const [dd, setDd] = useState(() => {
    const v = ddDateCache.get(customer.email);
    return v && v !== 'loading' ? v : null;
  });

  useEffect(() => {
    if (!isEmail || !customer.email) return;
    const key = customer.email;
    const cached = ddDateCache.get(key);
    if (cached && cached !== 'loading') { setDd(cached); return; }
    if (cached === 'loading') return;
    ddDateCache.set(key, 'loading');
    let alive = true;
    fetchDotdigitalSignup(key).then((r) => {
      const val = r || { signupDate: null, found: false };
      ddDateCache.set(key, val);
      if (alive) setDd(val);
    });
    return () => { alive = false; };
  }, [isEmail, customer.email]);

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
      <ChannelPill channel={customer.channel} />
      {isEmail && dd && dd.signupDate && (
        <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>newsletter since {fmtDate(dd.signupDate)}</span>
      )}
    </div>
  );
}

// Clickable in-range order # → opens Order Details in a NEW TAB (order_by_id
// resolves either bc_order_id or the internal id). Uses a real <a href> so
// middle-click / cmd-click / open-in-new-tab all behave natively; App.js reads
// the ?order= param on load.
function OrderLink({ order }) {
  const label = `#${order.number}`;
  const href = `${window.location.pathname}?page=order-details&order=${encodeURIComponent(order.linkId)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open order ${label} details in a new tab`}
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--orange)',
        textDecoration: 'underline',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </a>
  );
}

// Superscript reship count shown beside an order total, e.g. 1302⁺⁸⁷. Reships
// are excluded from the base count (revenue-accurate); the exponent makes the
// number reconcile with the live admin's raw "Orders Placed" count. No visible
// legend by design — the title attr explains it on hover.
function ReshipSup({ n, light }) {
  if (!n) return null;
  return (
    <sup
      title={`+${n} reship${n === 1 ? '' : 's'} excluded (counted by the live admin's Orders Placed)`}
      style={{ fontSize: '0.6em', fontWeight: 700, marginLeft: 1, color: light ? 'rgba(255,255,255,0.75)' : 'var(--gray-500)', cursor: 'help' }}
    >
      +{n}
    </sup>
  );
}

// CS Wins (formerly "Won Back") — every CS-attributed conversion in the range, in one collapsible
// panel. Deliberately separate from the four category cards: the CS "flip to VIP"
// admin toggle overwrites GroundShippingMembers.StartDate, which re-buckets a
// won-back ex-VIP into repeat_never_vip, so on the cards alone these customers are
// both scattered and mislabelled. "Emailed as" is the bucket the rep was actually
// looking at when they hit send — the only durable record of what the customer was.
function WonBackCard({ customers, expanded, onToggle, rangeText }) {
  const total = customers.length;
  const offerValue = customers.reduce((s, c) => s + (Number(c.outreachOffer) || 0), 0);
  const SENT_LABEL = {
    ex_vip: 'Ex-VIP',
    repeat_never_vip: 'Repeat, never VIP',
    new: 'Brand new',
    existing_vip: 'Existing VIP',
  };
  const th = { padding: '8px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'left', whiteSpace: 'nowrap' };
  const td = { padding: '10px 12px', whiteSpace: 'nowrap' };

  return (
    <div style={{ border: `1px solid ${total ? '#0b8043' : 'var(--gray-200, #e4e7ec)'}`, borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          background: expanded ? 'var(--gray-50, #f9fafb)' : '#fff', border: 'none',
          borderBottom: expanded ? '1px solid var(--gray-200, #e4e7ec)' : 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        {expanded ? <ChevronDown size={16} color="var(--gray-500)" /> : <ChevronRight size={16} color="var(--gray-500)" />}
        <Sparkles size={16} color={total ? '#0b8043' : 'var(--gray-400, #98a2b3)'} />
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>CS Wins</span>
        <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
          {total === 0
            ? 'no CS-attributed flips from this range yet'
            : `flipped to VIP by customer service during this range, after an offer email`}
        </span>
        <div style={{ marginLeft: 'auto', display: 'grid', gridTemplateColumns: '96px 120px', columnGap: 18, alignItems: 'center' }}>
          <Stat label="CS Wins" value={total > 0 ? `+${total}` : '0'} accent={total > 0} />
          <Stat label="Offer Value" value={money(offerValue)} />
        </div>
      </button>

      {expanded && (
        <div style={{ padding: total ? 0 : 16, overflowX: 'auto' }}>
          {total === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--gray-500)', textAlign: 'center', lineHeight: 1.6 }}>
              No CS wins flipped during {rangeText}.<br />
              Credit lands on the day CS flips the customer, whenever their order was placed.
            </div>
          ) : (
            <>
              <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-100, #f2f4f7)' }}>
                These have <strong>no VIP Club Membership line item</strong> — CS honored the offer by hand, so no order-based signup
                count can see them. Credited to the send that preceded the flip.
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50, #f9fafb)', color: 'var(--gray-500)' }}>
                    <th style={th}>Customer</th>
                    <th style={th}>Emailed as</th>
                    <th style={th}>Emailed</th>
                    <th style={th}>Rep</th>
                    <th style={th}>Flipped</th>
                    <th style={{ ...th, textAlign: 'right' }}>Offer</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr key={c.id} style={{ borderTop: '1px solid var(--gray-100, #f2f4f7)' }}>
                      <td style={{ ...td, whiteSpace: 'normal' }}>
                        <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.name}</div>
                        {c.email && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{c.email}</div>}
                      </td>
                      <td style={td}>
                        {c.sentCategory
                          ? <Badge color="#475467" bg="var(--gray-100, #f2f4f7)">{SENT_LABEL[c.sentCategory] || c.sentCategory}</Badge>
                          : <span style={{ fontSize: 11, color: 'var(--gray-400, #98a2b3)' }} title="Sent before the bucket was recorded">not recorded</span>}
                      </td>
                      <td style={{ ...td, fontSize: 12 }}>{fmtDate(c.outreachSentAt)}</td>
                      <td style={{ ...td, fontSize: 12 }}>{c.outreachRep || '—'}</td>
                      <td style={{ ...td, fontSize: 12, color: '#0b8043', fontWeight: 700 }}>{fmtDate(c.flippedOn)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {c.outreachOffer > 0
                          ? money(c.outreachOffer)
                          : <span style={{ color: '#0b8043', fontWeight: 700, fontSize: 11 }}>FREE</span>}
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, light, sub }) {
  const labelColor = light ? 'rgba(255,255,255,0.7)' : 'var(--gray-500)';
  const valueColor = accent ? 'var(--orange)' : (light ? '#fff' : 'var(--navy)');
  const subColor = light ? 'rgba(255,255,255,0.65)' : 'var(--gray-500)';
  return (
    <div style={{ textAlign: 'right', minWidth: 64 }}>
      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: labelColor }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: valueColor }}>{value}</div>
      {sub && <div style={{ fontSize: 10, fontWeight: 600, color: subColor, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function SortableTh({ col, active, dir, onClick }) {
  const right = col.align === 'right';
  return (
    <th
      onClick={onClick}
      title="Click to sort"
      style={{
        padding: '8px 12px',
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        textAlign: right ? 'right' : 'left',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        userSelect: 'none',
        color: active ? 'var(--navy)' : 'inherit',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexDirection: right ? 'row-reverse' : 'row' }}>
        {col.label}
        {active ? (
          dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
        ) : (
          <ArrowDown size={12} style={{ opacity: 0.22 }} />
        )}
      </span>
    </th>
  );
}

const Td = ({ children, right, accent }) => (
  <td style={{ padding: '10px 12px', textAlign: right ? 'right' : 'left', whiteSpace: 'nowrap', fontWeight: accent ? 700 : 400, color: accent ? 'var(--orange)' : 'inherit' }}>{children}</td>
);

const Badge = ({ children, color, bg }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10, fontWeight: 700, color, background: bg, padding: '1px 7px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: 0.3 }}>{children}</span>
);

// VIP-invite email composer. Builds the personalized email, lets the rep review
// / tweak it, then generates an .eml that pops open in classic Outlook ready to
// send from the rep's account. Logs the send so the list tracks who's contacted.
function OutreachModal({ customer, rep, todayIso, alreadyEmailed, rangeStart, rangeEnd, mode, onClose, onSent }) {
  const built = useMemo(() => buildOutreachEmail(customer, rep, todayIso), [customer, rep, todayIso]);
  const [to, setTo] = useState(built.to);
  const [subject, setSubject] = useState(built.subject);
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState('');
  const bodyRef = useRef(null);
  const offer = built.offer;

  // Seed the editable body once per built email (uncontrolled — we read innerHTML
  // back on send so the rep's inline tweaks travel into the .eml).
  useEffect(() => { if (bodyRef.current) bodyRef.current.innerHTML = built.html; }, [built]);
  // Settle whether the tracking host answers before she clicks, so the download is not held up.
  useEffect(() => { trackingReady(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ⚠️ WRITES A NOTE ON THE ORDER IN THE LIVE ADMIN. Rachael, 2026-08-31: "can this
  // also generate a note on Add order note... to say 'offered VIP membership for
  // ($cost from the email)'? it will need to select our name too for who is emailing
  // the VIP offer." The price is the one the email actually quotes, so the note and
  // the offer can never disagree, and the employee is the signed-in rep.
  // ⚠️ FIRE AND FORGET, DELIBERATELY. By the time this runs the .eml has already been
  // handed to Outlook and the send is logged - failing the whole action because a note
  // did not post would be the tail wagging the dog. It needs the INTERNAL order id;
  // with no order in range there is nothing to attach it to and it does nothing.
  const noteOnOrder = () => {
    const internalId = built.latestOrderInternalId;
    if (!internalId) return;
    const text = offer.free
      ? `Offered VIP membership free (would have saved ${money(offer.saved)})`
      : `Offered VIP membership for ${money(offer.price)}`;
    addOrderNote(internalId, text, rep.name).catch(() => {
      setWarn('The invite is logged, but the note could not be added to the order in the admin. Add it by hand if you need it there.');
    });
  };

  // ⚠️ THIS IS THE CLAIM AND IT RUNS BEFORE THE .eml IS BUILT. It used to run after the download,
  // so a refusal arrived when the draft was already in Outlook - which is how 50 win-back
  // customers got the same offer twice on 2026-09-16. The server refuses a second offer to the
  // same customer inside 24 hours (vip_outreach_add), so a blocked claim means nothing was
  // written and nothing reached Outlook. It returns the refusal rather than throwing.
  const claimSend = async (track) => {
    const res = await logVipOutreach({
      customerId: customer.id, email: to, orderNumber: built.latestOrder || null,
      wouldHaveSaved: offer.saved, offerPrice: offer.price,
      adminId: rep.adminId, repName: rep.name, repEmail: rep.email,
      rangeStart, rangeEnd, mode,
      // Bucket the rep was looking at when they hit send. Recorded here because
      // a later CS "flip to VIP" overwrites GroundShippingMembers.StartDate, which
      // erases the evidence the customer was ever lapsed — so the category cannot
      // be reconstructed after the fact from vip_memberships.
      category: customer.category || null,
      // Only a send that actually carried the pixel counts toward an open rate.
      sendToken: track ? track.token : null,
      pixelEmbedded: !!(track && track.embedded),
    });
    if (res && res.blocked) return res;
    onSent(customer.id, { sentAt: new Date().toISOString(), repName: rep.name });
    return null;
  };

  const handleOpen = async () => {
    setBusy(true);
    // Rachael, 2026-09-16: replies fell and nobody could tell junk from ignored. The pixel is
    // appended at build time, outside the editable body, and only once its host answers.
    const token = newSendToken();
    const embedded = await trackingReady();
    let blocked;
    try { blocked = await claimSend({ token, embedded }); }
    catch (_) {
      setWarn('We could not record this send, so the email has not been opened. Try again in a moment.');
      setBusy(false);
      return;
    }
    if (blocked) { setWarn(blocked.reason); setBusy(false); return; }
    const body = bodyRef.current ? bodyRef.current.innerHTML : built.html;
    const html = embedded ? body + pixelTag(token) : body;
    const eml = buildEml({ fromEmail: rep.email || 'customerservice@crabplace.com', fromName: rep.name, to, subject, html });
    downloadEml(`VIP-${slugName(customer.name)}${built.latestOrder ? '-' + built.latestOrder : ''}.eml`, eml);
    noteOnOrder();
    onClose();
  };

  // Sent some other way, so no pixel went with it and it must not count toward an open rate.
  const markOnly = async () => {
    setBusy(true);
    try {
      const blocked = await claimSend(null);
      if (blocked) { setWarn(blocked.reason); setBusy(false); return; }
      onClose();
    } catch (_) { setBusy(false); }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.55)', zIndex: 4000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' };
  const panel = { width: '100%', maxWidth: 720, background: '#fff', borderRadius: 12, boxShadow: '0 20px 48px rgba(16,24,40,0.28)', overflow: 'hidden' };
  const fieldRow = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px', borderBottom: '1px solid var(--gray-100, #f2f4f7)' };
  const fieldLabel = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', width: 56, flexShrink: 0 };
  const fieldInput = { flex: 1, fontSize: 13, padding: '6px 8px', border: '1px solid var(--gray-200, #e4e7ec)', borderRadius: 6, fontFamily: 'inherit' };

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={panel}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px', background: 'var(--navy)', color: '#fff' }}>
          <Mail size={18} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>VIP Club invitation</div>
            <div style={{ fontSize: 12, opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {customer.name}{customer.email ? ` · ${customer.email}` : ''}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.75 }}>Would’ve saved</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              {money(offer.saved)} <span style={{ opacity: 0.7 }}>→</span>{' '}
              {offer.free
                ? <span style={{ color: '#7CE0A6' }}>FREE membership</span>
                : <span style={{ color: 'var(--orange-light, #EA733D)' }}>{money(offer.price)}</span>}
            </div>
          </div>
          <button type="button" onClick={onClose} title="Close" style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: 8, width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <XIcon size={16} />
          </button>
        </div>

        {alreadyEmailed && (
          <div style={{ padding: '8px 18px', fontSize: 12, color: '#7a4a00', background: '#fff7e6', borderBottom: '1px solid #ffe4b3' }}>
            Already emailed {alreadyEmailed.sentAt ? fmtDate(alreadyEmailed.sentAt) : ''}{alreadyEmailed.repName ? ` by ${alreadyEmailed.repName}` : ''}. Sending again is fine.
          </div>
        )}

        {/* From / To / Subject */}
        <div style={{ ...fieldRow }}>
          <span style={fieldLabel}>From</span>
          <span style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600 }}>
            {rep.name}{rep.email ? ` <${rep.email}>` : ''}
          </span>
        </div>
        <div style={fieldRow}>
          <span style={fieldLabel}>To</span>
          <input style={fieldInput} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div style={fieldRow}>
          <span style={fieldLabel}>Subject</span>
          <input style={fieldInput} value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>

        {/* Editable body */}
        <div style={{ padding: '12px 18px' }}>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6 }}>
            Review and edit if you like, then open it in Outlook to send.
          </div>
          <div
            ref={bodyRef}
            contentEditable
            suppressContentEditableWarning
            style={{ border: '1px solid var(--gray-200, #e4e7ec)', borderRadius: 8, padding: '14px 16px', maxHeight: 320, overflowY: 'auto', outline: 'none', background: '#fff' }}
          />
        </div>

        {warn && (
          <div style={{ margin: '0 18px 12px', padding: '8px 12px', fontSize: 12, color: '#7a4a00', background: '#fff7e6', border: '1px solid #ffe4b3', borderRadius: 8 }}>
            {warn}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderTop: '1px solid var(--gray-100, #f2f4f7)', background: 'var(--gray-50, #f9fafb)' }}>
          <span style={{ fontSize: 11, color: 'var(--gray-500)', flex: 1 }}>
            Opens in Outlook as a ready-to-send message from your account. We’ll mark this customer as emailed.
          </span>
          {warn && (
            <button type="button" onClick={markOnly} disabled={busy} style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', background: '#fff', border: '1px solid var(--gray-300, #d0d5dd)', borderRadius: 8, padding: '9px 14px', cursor: 'pointer' }}>
              Mark as emailed
            </button>
          )}
          <button type="button" onClick={onClose} disabled={busy} style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-600, #475467)', background: '#fff', border: '1px solid var(--gray-300, #d0d5dd)', borderRadius: 8, padding: '9px 14px', cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="button" onClick={handleOpen} disabled={busy || !to} style={{ fontSize: 13, fontWeight: 700, color: '#fff', background: to ? 'var(--orange)' : 'var(--gray-300, #d0d5dd)', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: busy || !to ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Mail size={14} /> {busy ? 'Working…' : 'Open in Outlook'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delegation UI ─────────────────────────────────────────────────────────────

// The little "Linda · due 3:00 PM" tag under a customer who has been handed out.
// Turns red once the deadline passes, which is the same moment the reminder cron
// escalates it — one signal, two places, so the screen and the email agree.
function AssignChip({ a }) {
  if (!a || !a.assigneeName) return null;
  const overdue = a.dueAt && new Date(a.dueAt).getTime() < Date.now();
  const first = String(a.assigneeName).split(' ')[0];
  return (
    <div style={{ marginTop: 4 }}>
      {/* ⚠️ FILLED, NOT TINTED. Rachael, 2026-09-01: "can we make the name assignments
          look like the 'already a VIP member' or be similar with how bold it is so
          that it sticks out more to us when we're scrolling through?" The old tag was
          navy on pale grey, which is the quietest thing on a row that also carries
          solid orange and solid purple badges - so the one label telling her the row
          is HERS was the one she could not see. Same shape and weight as Badge now:
          white on a solid fill, uppercase, so it reads at scrolling speed. Red when
          it has gone past due, which is the same signal the reminder cron escalates
          on - one meaning, two places. */}
      <span
        title={'Assigned to ' + a.assigneeName + (a.assignedBy ? ' by ' + a.assignedBy : '')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
          textTransform: 'uppercase', letterSpacing: 0.3,
          color: '#fff',
          background: overdue ? '#b42318' : 'var(--navy)',
          borderRadius: 999, padding: '2px 9px',
        }}
      >
        {overdue ? <AlertTriangle size={10} /> : <Clock size={10} />}
        {first}{a.dueAt ? ' · ' + (overdue ? 'was due ' : 'due ') + fmtDue(a.dueAt) : ''}
      </span>
    </div>
  );
}

// Who is working the queue: sent and replied per rep, plus what is still out.
// Counted by SEND DATE, which is a different axis from the cards below (order or
// delivery date) — labelled, rather than left to look like the same number.
function OutreachScoreboard({ rows, assignMap, rangeText, mineOnly, onToggleMine, repName, myOpen, onGoToMyOpen, focusCount = 0, onClearFocus }) {
  const open = [];
  const overdue = [];
  Object.values(assignMap || {}).forEach((a) => {
    if (!a || a.done) return;
    open.push(a);
    if (a.dueAt && new Date(a.dueAt).getTime() < Date.now()) overdue.push(a);
  });
  const totalSent = (rows || []).reduce((n, r) => n + (Number(r.sent) || 0), 0);
  const totalReplied = (rows || []).reduce((n, r) => n + (Number(r.replied) || 0), 0);
  // ⚠️ myOpen counts work on ANY date, so the strip has to survive a range with
  // nothing in it - that empty range is exactly when somebody needs telling that
  // they still owe three emails from last week.
  const owed = myOpen && (myOpen.open > 0) ? myOpen : null;
  if (!totalSent && !open.length && !owed) return null;

  const replyRate = totalSent > 0 ? Math.round((totalReplied / totalSent) * 100) : 0;

  return (
    <div style={{ border: '1px solid var(--gray-200, #e4e7ec)', borderRadius: 10, background: '#fff', padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: totalSent ? 12 : 0 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--navy)' }}>VIP invitations</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)' }}>
          sent {rangeText}
        </span>
        {overdue.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: '#b42318', background: '#fef3f2', border: '1px solid #fda29b', borderRadius: 999, padding: '2px 10px' }}>
            <AlertTriangle size={11} /> {overdue.length} past due
          </span>
        )}
        {open.length > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--orange)' }}>
            {open.length} assigned, not sent
          </span>
        )}
        {/* Rachael, 2026-09-01: "take us right to them rather than having to change
            the date range." Counts every open invitation assigned to you whatever day
            it belongs to, and clicking it moves the range to cover them. */}
        {/* ⚠️ A FILTER NOBODY CAN SEE IS A BUG REPORT WAITING TO HAPPEN. The whole
            reason this exists is that Rachael could not tell what the screen was
            showing her, so when the list is pinned it says so and offers the way out
            in the same breath. */}
        {focusCount > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11,
            fontWeight: 700, color: '#fff', background: 'var(--navy)',
            borderRadius: 999, padding: '3px 6px 3px 11px',
          }}>
            Showing your {focusCount} to send
            <button
              type="button"
              onClick={onClearFocus}
              title="Show everything in this date range again"
              style={{
                fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                color: 'var(--navy)', background: '#fff', border: 'none',
                borderRadius: 999, padding: '1px 8px',
              }}
            >clear</button>
          </span>
        )}
        {owed && focusCount === 0 && (
          <button
            type="button"
            onClick={onGoToMyOpen}
            title={`You have ${owed.open} invitation${owed.open === 1 ? '' : 's'} still to send`
              + (owed.firstDate ? `, from ${owed.firstDate}${owed.lastDate && owed.lastDate !== owed.firstDate ? ' to ' + owed.lastDate : ''}` : '')
              + '. Click to jump to them.'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
              fontFamily: 'inherit', cursor: 'pointer',
              color: owed.pastDue > 0 ? '#b42318' : 'var(--orange)',
              background: owed.pastDue > 0 ? '#fef3f2' : '#fff7ed',
              border: '1px solid ' + (owed.pastDue > 0 ? '#fda29b' : '#fed7aa'),
              borderRadius: 999, padding: '3px 11px',
            }}
          >
            {owed.pastDue > 0 && <AlertTriangle size={11} />}
            {owed.pastDue > 0
              ? `${owed.pastDue} past due` + (owed.open > owed.pastDue ? ` · ${owed.open - owed.pastDue} pending` : '')
              : `${owed.open} pending`}
            <span style={{ fontWeight: 600, opacity: 0.8 }}>· go to them</span>
          </button>
        )}
        <button
          type="button"
          onClick={onToggleMine}
          title="Show only the customers assigned to me (unassigned ones stay visible)"
          style={{
            marginLeft: 'auto', fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
            color: mineOnly ? '#fff' : 'var(--navy)',
            background: mineOnly ? 'var(--navy)' : '#fff',
            border: '1px solid var(--navy)', borderRadius: 999, padding: '4px 12px', cursor: 'pointer',
          }}
        >
          {mineOnly ? 'Showing mine' : 'Mine only'}{repName ? ' (' + String(repName).split(' ')[0] + ')' : ''}
        </button>
      </div>

      {totalSent > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 22, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-400, #98a2b3)' }}>Sent</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy)', lineHeight: 1.1 }}>{totalSent}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-400, #98a2b3)' }}>Replied</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: totalReplied > 0 ? '#0b8043' : 'var(--gray-400, #98a2b3)', lineHeight: 1.1 }}>
              {totalReplied}
              {totalSent > 0 && <span style={{ fontSize: 12, fontWeight: 700, marginLeft: 5, color: 'var(--gray-500)' }}>{replyRate}%</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(rows || []).map((r) => (
              <div
                key={r.rep_name}
                title={r.rep_name + ': ' + r.sent + ' sent, ' + r.replied + ' replied'}
                style={{ border: '1px solid var(--gray-200, #e4e7ec)', borderRadius: 8, padding: '6px 12px', minWidth: 92 }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)' }}>{String(r.rep_name).split(' ')[0]}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)', lineHeight: 1.2 }}>{r.sent}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700, color: Number(r.replied) > 0 ? '#0b8043' : 'var(--gray-400, #98a2b3)' }}>
                  <Reply size={10} /> {r.replied} back
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {totalSent > 0 && (
        <div style={{ fontSize: 10, color: 'var(--gray-400, #98a2b3)', marginTop: 10, lineHeight: 1.5 }}>
          Replies are counted by watching each rep&rsquo;s own mailbox for a message back from that customer
          within 30 days of the invitation. Read-only, and it never opens a message body.
        </div>
      )}
    </div>
  );
}

// Rachael's delegation overlay for one block: tick customers, pick who gets them,
// set the deadline. Deliberately scoped to ONE category and ONE day, because that
// is how the work is actually handed out ("Linda, take the ex-VIPs today").
function AssignPanel({ cat, categoryKey, assignees, assignMap, emailedMap, workDate, mode, assignedByAdminId, isDelegator, onClose, onChanged }) {
  // currentlyVip keeps a paying member out of the pool entirely, so they cannot
  // be ticked, assigned, or listed in the "here is your batch" email. The
  // category is a HISTORY (what they were when they ordered) and this is the
  // WORKLIST (who still needs inviting today) — the two genuinely disagree
  // whenever a queue is worked days after the fact, which is the normal case.
  const pool = useMemo(() => (cat?.customers || []).filter(
    (c) => c.email && !emailedMap[String(c.id)] && !c.signedUpToday && !c.currentlyVip
  ), [cat, emailedMap]);

  // Customers who were already emailed (by any rep) but still carry an open
  // assignment — the normal SENT_LATERAL only closes an assignment when the
  // send happened AFTER the assignment was created, so a prior send leaves a
  // stale row. A delegator can clear these manually.
  const staleAssigned = useMemo(() => {
    if (!isDelegator) return [];
    return (cat?.customers || []).filter((c) => {
      const id = String(c.id);
      const a = assignMap[id];
      return emailedMap[id] && a && !a.done;
    });
  }, [cat, emailedMap, assignMap, isDelegator]);

  const [stalePicked, setStalePicked] = useState(() => new Set());

  const doClearStale = async () => {
    const ids = [...stalePicked];
    if (!ids.length) return;
    setBusy(true); setMsg(null);
    try {
      await clearOutreachAssignments(ids, workDate);
      setMsg({ ok: true, text: ids.length + ' stale assignment' + (ids.length > 1 ? 's' : '') + ' cleared.' });
      setStalePicked(new Set());
      onChanged && onChanged();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Could not clear.' });
    }
    setBusy(false);
  };

  const [picked, setPicked] = useState(() => new Set());
  // A SET, not one id: ticking two people turns Assign into a split between
  // them. The earlier design round-robined across everyone holding the
  // Customer Service role, which is six people, not the three who do this.
  const [chosenReps, setChosenReps] = useState(() => new Set());
  const [dueHour, setDueHour] = useState(17);
  const [dueDay, setDueDay] = useState(0);
  const [remind, setRemind] = useState(60);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!chosenReps.size && assignees.length) setChosenReps(new Set([String(assignees[0].admin_id)]));
  }, [assignees, chosenReps]);
  const toggleRep = (id) => setChosenReps((s0) => {
    const n = new Set(s0);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const toggle = (id) => setPicked((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allPicked = pool.length > 0 && picked.size === pool.length;
  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(pool.map((c) => String(c.id))));

  const chosen = pool.filter((c) => picked.has(String(c.id)));
  // True when every selected customer already has a note-rep who is on the
  // assignees list — assignment can proceed without a manual rep selection.
  const allNoteOwned = chosen.length > 0 && chosen.every((c) => {
    const noteId = c.noteRepAdminId ? String(c.noteRepAdminId) : null;
    return noteId && assignees.some((u) => String(u.admin_id) === noteId);
  });
  const dueIso = dueAtIso(dueHour, dueDay);
  const dueInPast = new Date(dueIso).getTime() < Date.now();

  const payloadFor = (customers, adminId) => ({
    customers: customers.map((c) => ({ id: c.id, name: c.name, email: c.email, category: categoryKey })),
    assigneeAdminId: adminId,
    assignedByAdminId,
    workDate,
    mode,
    dueAt: dueIso,
    remindMinutes: remind,
  });

  // Customers tagged "Note follow-up: <rep>" go straight to that rep.
  // Everyone else is dealt round-robin across the ticked reps.
  const doAssign = async () => {
    const reps = [...chosenReps];
    if (!chosen.length || (!reps.length && !allNoteOwned)) return;
    setBusy(true); setMsg(null);
    try {
      const emailed = [];
      const unreached = [];
      // Customers the server refused because they have joined the club since
      // this queue was built. Reported rather than swallowed, so the count on
      // screen and the count in the rep's email always agree.
      const alreadyVip = [];
      const buckets = new Map();
      const counted = new Map();
      let rrIdx = 0;
      chosen.forEach((c) => {
        const noteId = c.noteRepAdminId ? String(c.noteRepAdminId) : null;
        const noteInList = noteId && assignees.some((u) => String(u.admin_id) === noteId);
        const k = noteInList ? noteId : reps[rrIdx++ % reps.length];
        if (!buckets.has(k)) buckets.set(k, []);
        buckets.get(k).push(c);
      });
      for (const [adminId, list] of buckets) {
        const saved = await assignOutreach(payloadFor(list, adminId));
        // The handler used to answer with a bare array and now answers with
        // { assigned, skipped }. Both shapes are read so an old cached bundle
        // in somebody's open tab keeps working against the new function.
        const rows = Array.isArray(saved) ? saved : (saved?.assigned || []);
        (Array.isArray(saved) ? [] : (saved?.skipped || []))
          .forEach((x) => alreadyVip.push(x.name || x.id));
        // Rachael asked to be able to SEE that they were told, rather than
        // walking over to say so. Assigning yourself sends nothing on purpose,
        // so that is not a failure to report.
        const r0 = rows[0] || null;
        const who = assignees.find((x) => String(x.admin_id) === String(adminId));
        const nm = String(who?.staff_name || 'rep').split(' ')[0];
        if (r0 && !r0.notify_self) (r0.notify_ok ? emailed : unreached).push(nm);
        counted.set(adminId, rows.length);
      }
      const parts = [...buckets.entries()].map(([id, list]) => {
        const who = assignees.find((x) => String(x.admin_id) === String(id));
        // The number ACTUALLY handed out, which is not always the number ticked.
        const n = counted.has(id) ? counted.get(id) : list.length;
        return String(who?.staff_name || 'rep').split(' ')[0] + ' ' + n;
      }).filter((p) => !/ 0$/.test(p));
      const told = emailed.length ? ' ' + emailed.join(' and ') + ' emailed.' : '';
      // Never claim a send that did not happen: the deadline job re-tries an
      // un-notified handoff within ten minutes, and saying so beats silence.
      const missed = unreached.length
        ? ' Could not email ' + unreached.join(' and ') + ' yet, CPx will retry within 10 minutes.'
        : '';
      // Named, not just counted: "1 skipped" invites a hunt for which one.
      const skippedNote = alreadyVip.length
        ? ' Skipped ' + alreadyVip.join(', ') + ' — already ' + (alreadyVip.length === 1 ? 'a VIP member' : 'VIP members') + '.'
        : '';
      setMsg({
        ok: true,
        text: (parts.length ? parts.join(', ') + ' — due ' + fmtDue(dueIso) + '.' : 'Nothing to assign.')
          + told + missed + skippedNote,
      });
      setPicked(new Set());
      onChanged && onChanged();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Could not save the assignment.' });
    }
    setBusy(false);
  };

  const doUnassign = async () => {
    if (!chosen.length) return;
    setBusy(true); setMsg(null);
    try {
      await clearOutreachAssignments(chosen.map((c) => String(c.id)), workDate);
      setMsg({ ok: true, text: chosen.length + ' taken back off the list.' });
      setPicked(new Set());
      onChanged && onChanged();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Could not unassign.' });
    }
    setBusy(false);
  };

  const doPush = async () => {
    if (!chosen.length) return;
    setBusy(true); setMsg(null);
    try {
      await pushOutreachAssignments(chosen.map((c) => String(c.id)), { hours: 24 });
      setMsg({ ok: true, text: chosen.length + ' pushed to the same time tomorrow.' });
      setPicked(new Set());
      onChanged && onChanged();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Could not push the deadline.' });
    }
    setBusy(false);
  };

  const chip = (active) => ({
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
    padding: '5px 12px', borderRadius: 999,
    border: '1px solid ' + (active ? 'var(--orange)' : 'var(--gray-300, #d0d5dd)'),
    background: active ? 'var(--orange)' : '#fff',
    color: active ? '#fff' : 'var(--navy)',
  });

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 12, width: 'min(760px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', background: 'var(--navy)', color: '#fff' }}>
          <Users size={17} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Assign &mdash; {cat?.label}</div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>
              {pool.length} still to be emailed{workDate ? ' · ' + fmtDate(workDate + 'T12:00:00') : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4 }}
          ><XIcon size={18} /></button>
        </div>

        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gray-200, #e4e7ec)', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', width: 62 }}>Who</span>
            {assignees.map((u) => (
              <button key={u.admin_id} type="button" onClick={() => toggleRep(String(u.admin_id))} style={chip(chosenReps.has(String(u.admin_id)))}>
                {String(u.staff_name || '').split(' ')[0]}
              </button>
            ))}
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
              {chosenReps.size > 1 ? 'Split evenly between the ' + chosenReps.size + ' ticked' : 'Tick a second name to split the list'}
            </span>
            {!assignees.length && <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>No customer-service users found.</span>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', width: 62 }}>Due</span>
            {[[12, 0, 'Noon'], [15, 0, '3:00 PM'], [17, 0, '5:00 PM'], [12, 1, 'Tomorrow noon'], [17, 1, 'Tomorrow 5:00']].map(([h, d, label]) => (
              <button key={label} type="button" onClick={() => { setDueHour(h); setDueDay(d); }} style={chip(dueHour === h && dueDay === d)}>{label}</button>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', width: 62 }}>Remind</span>
            {[[60, '1 hour before'], [120, '2 hours before'], [30, '30 min before']].map(([m, label]) => (
              <button key={m} type="button" onClick={() => setRemind(m)} style={chip(remind === m)}>{label}</button>
            ))}
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
              We email the rep if it is still unsent, then flag it to you once it goes past due.
            </span>
          </div>

          {dueInPast && (
            <div style={{ fontSize: 12, fontWeight: 600, color: '#b42318' }}>
              That time has already passed today &mdash; pick a later one, or it will escalate immediately.
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 18px' }}>
          {pool.length === 0 ? (
            <div style={{ padding: '24px 0', fontSize: 13, color: 'var(--gray-500)', textAlign: 'center' }}>
              Everyone in this block has been emailed already, or has no address on file.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ width: 30, padding: '8px 0' }}>
                    <input type="checkbox" checked={allPicked} onChange={toggleAll} title="Select all" />
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-400, #98a2b3)' }}>Customer</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-400, #98a2b3)' }}>Would&rsquo;ve saved</th>
                  <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-400, #98a2b3)' }}>Assigned</th>
                </tr>
              </thead>
              <tbody>
                {pool.map((c) => {
                  const id = String(c.id);
                  const a = assignMap[id];
                  const overdue = a?.dueAt && new Date(a.dueAt).getTime() < Date.now();
                  return (
                    <tr key={id} style={{ borderTop: '1px solid var(--gray-100, #f2f4f7)' }}>
                      <td style={{ padding: '8px 0' }}>
                        <input type="checkbox" checked={picked.has(id)} onChange={() => toggle(id)} />
                      </td>
                      <td style={{ padding: '8px 6px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{c.email}</div>
                        {c.noteRep && (
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#c4320a', marginTop: 2 }}>
                            Note → {String(c.noteRep).split(' ')[0]}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#d92d20' }}>
                        {money(c.whsPeriod)}
                      </td>
                      <td style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, fontWeight: 700, color: overdue ? '#b42318' : 'var(--gray-500)' }}>
                        {a?.assigneeName
                          ? String(a.assigneeName).split(' ')[0] + (a.dueAt ? ' · ' + fmtDue(a.dueAt) : '')
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {isDelegator && staleAssigned.length > 0 && (
            <div style={{ marginTop: 14, borderTop: '1px solid var(--gray-100, #f2f4f7)', paddingTop: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-400, #98a2b3)', marginBottom: 6 }}>
                Already emailed — stale assignment
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {staleAssigned.map((c) => {
                    const id = String(c.id);
                    const a = assignMap[id];
                    return (
                      <tr key={id} style={{ borderTop: '1px solid var(--gray-100, #f2f4f7)' }}>
                        <td style={{ padding: '8px 0', width: 30 }}>
                          <input type="checkbox" checked={stalePicked.has(id)}
                            onChange={() => setStalePicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; })} />
                        </td>
                        <td style={{ padding: '8px 6px' }}>
                          <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{c.email}</div>
                        </td>
                        <td style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, color: 'var(--gray-500)' }}>
                          {a?.assigneeName ? 'Assigned to ' + String(a.assigneeName).split(' ')[0] : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
                <button type="button" onClick={doClearStale} disabled={busy || !stalePicked.size}
                  style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 700, padding: '7px 12px', borderRadius: 6, border: '1px solid var(--gray-300, #d0d5dd)', background: '#fff', color: stalePicked.size ? '#b42318' : 'var(--gray-400, #98a2b3)', cursor: stalePicked.size ? 'pointer' : 'not-allowed' }}
                >Clear assignment</button>
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                  {stalePicked.size ? stalePicked.size + ' selected' : 'Tick a name to clear the assignment'}
                </span>
              </div>
            </div>
          )}
        </div>

        <div style={{ borderTop: '1px solid var(--gray-200, #e4e7ec)', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {msg && (
            <span style={{ fontSize: 12, fontWeight: 600, color: msg.ok ? '#0b8043' : '#b42318', width: '100%' }}>{msg.text}</span>
          )}
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>{picked.size} selected</span>
          <button type="button" onClick={doUnassign} disabled={busy || !picked.size}
            style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 700, padding: '7px 12px', borderRadius: 6, border: '1px solid var(--gray-300, #d0d5dd)', background: '#fff', color: 'var(--gray-500)', cursor: picked.size ? 'pointer' : 'not-allowed' }}
          >Unassign</button>
          <button type="button" onClick={doPush} disabled={busy || !picked.size}
            title="Same time tomorrow, and the reminder re-arms for the new deadline"
            style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 700, padding: '7px 12px', borderRadius: 6, border: '1px solid var(--gray-300, #d0d5dd)', background: '#fff', color: 'var(--navy)', cursor: picked.size ? 'pointer' : 'not-allowed' }}
          >Push to tomorrow</button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button type="button" onClick={doAssign} disabled={busy || !picked.size || (!chosenReps.size && !allNoteOwned)}
              style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 800, padding: '8px 18px', borderRadius: 6, border: 'none', background: picked.size ? 'var(--orange)' : 'var(--gray-200, #e4e7ec)', color: picked.size ? '#fff' : 'var(--gray-400, #98a2b3)', cursor: picked.size ? 'pointer' : 'not-allowed' }}
            >{busy ? 'Saving…' : (chosenReps.size > 1 ? 'Split ' : 'Assign ') + (picked.size || '')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
